import axios from 'axios';
import $ from 'jquery';
import get from 'lodash/get';
import _ from 'underscore';

import RetailManager from 'app/retail_manager/RetailManager';
import BaseObject from 'app/src/base/BaseObject';
import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import CreditAccount from 'app/src/data/models/CreditAccount';
import Customer from 'app/src/data/models/Customer';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import PaymentModel from 'app/src/data/models/Payment';
import PaymentCollection from 'app/src/data/models/PaymentCollection';
import PaymentType from 'app/src/data/models/PaymentType';
import Sale from 'app/src/data/models/Sale';
import SaleLine from 'app/src/data/models/SaleLine';
import SalePayment from 'app/src/data/models/SalePayment';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import Filter from 'app/src/data/models/utils/Filter';
import Localize from 'app/src/localize/Localize';
import CashPayment from 'app/src/payments/CashPayment';
import {
  calculateCashRoundingAmount,
  calculateCashRoundingDelta,
  CashRoundingOptions,
  fetchCashRoundingOptions,
} from 'app/src/payments/CashRoundingService';
import DepositManager from 'app/src/payments/DepositManager';
import DepositPayment from 'app/src/payments/DepositPayment';
import GiftCardPayment from 'app/src/payments/GiftCardPayment';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentList from 'app/src/payments/PaymentList';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import { LsInvoicingInvoicePaymentTime } from 'root/apps/QuoteMachineIntegration/datadogEventConstants';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import PaymentService from 'root/apps/QuoteMachineIntegration/services/PaymentService';
import DepositInstructionService from 'root/apps/QuoteMachineIntegration/services/v1/DepositInstructionService';
import DepositUsageIntentService from 'root/apps/QuoteMachineIntegration/services/v1/DepositUsageIntentService';
import InvoicePaymentInstructionService from 'root/apps/QuoteMachineIntegration/services/v1/InvoicePaymentInstructionService';
import InvoiceService from 'root/apps/QuoteMachineIntegration/services/v1/InvoiceService';
import RefundIntentService, {
  buildCustomerFromData,
  buildEmployeeFromData,
  buildLocationFromShopData,
} from 'root/apps/QuoteMachineIntegration/services/v1/RefundIntentService';
import ReturnInstructionService from 'root/apps/QuoteMachineIntegration/services/v1/ReturnInstructionService';
import { getInvoicingPayload } from 'root/apps/QuoteMachineIntegration/utils/InvoicingPayload';
import {
  GetPendingCreditAccountPaymentAmount,
  getProcessedPaymentsFromSale,
  getSalePaymentsForInvoice,
} from 'root/apps/QuoteMachineIntegration/utils/Payment';
import { datadogRum } from 'root/datadog/datadogRum';
import Modules from 'shared_modules/Modules';
import Monitoring from 'shared_modules/Monitoring';

let currentSaleManagerInstance = null;

/**
 * Clears local invoice/deposit pay options and deletes the invoice payment instruction on invoicing-api
 * when the cashier removes the customer from the sale (before redirect to clear_customer).
 * Call and await this before navigation so the DELETE completes.
 *
 * @returns {Promise<void>}
 */
export async function clearInvoicingStateAfterCustomerRemoval() {
  if (!currentSaleManagerInstance) {
    return;
  }
  const sm = currentSaleManagerInstance;
  sm._invoicePaymentOptions = undefined;
  sm._depositPaymentOptions = undefined;

  const creditAccountID = sm._attachedCustomer?.get?.('creditAccountID');
  const saleId = sm._sale?.id != null ? `${sm._sale.id}` : null;

  if (!Modules.hasModule('invoicing.api.v1.payment_instructions') || !creditAccountID || !saleId) {
    return;
  }

  try {
    await InvoicePaymentInstructionService.deleteInvoicePaymentInstruction(creditAccountID, saleId);
  } catch (error) {
    datadogRum.addError(error, {
      message: 'Failed to delete invoice payment instruction after customer removal',
      creditAccountID,
      saleId,
      flow: 'remove_customer',
    });
    throw error;
  }
}

// events triggered by the SaleManager
const _Events = {
  PAYMENT_TOTAL_CHANGED: 'paymentsRecalculated',
  CALCTIPS_CHANGED: 'calcTipsChange',
  CALCSURCHARGES_CHANGED: 'calcSurchargesChange',
  BALANCE_CHANGED: 'balanceChanged',
  CHANGE_DUE_CHANGED: 'changeDueChanged',
  CASH_CHANGED: 'cashChanged',
  DEPOSITS_CHANGED: 'depositsChanged',
  SALE_UPDATING: 'saleUpdating',
  SALE_REFRESHING: 'saleRefreshing',
  SALE_REFRESHED: 'saleRefreshed',
  COMPLETE_SALE_STARTED: 'completeSaleStarted',
  COMPLETE_SALE_FAILED: 'completeSaleFailed',
  DEPOSIT_PAYMENT_CHANGED: 'depositPaymentChanged',
};

// used to identify errors to users of the SaleManager
const _Errors = {
  NO_ERROR: 1, // no error has occurred,
  USER_FACING: 2, // used to identify that the error that may be user facing
  INTERNAL: 500, // and internal error has occurred
};

const _GIFT_CARD_MODULE_NAME = 'gift_card';

/**
 * Class definition of a Sale Manager status.  Used to respond to users
 * of the Sale Manager to report errors.  If the error type is USER_FACING then
 * the message will be localized.
 */
class SaleManagerStatus {
  constructor(error = _Errors.NO_ERROR, msg = '') {
    this._error = error;
    this._msg = error === _Errors.USER_FACING ? Localize.translate(msg) : msg;
  }

  /**
   * getter: Retrieves the error code from the message.
   *
   * @returns {number}    The error code
   */
  get error() {
    return this._error;
  }

  /**
   * getter
   * @returns {string}    The localized string
   */
  get msg() {
    return this._msg;
  }

  /**
   * will check if the sale status is of type NO ERROR
   * @returns {boolean}
   */
  isError() {
    return this._error !== _Errors.NO_ERROR;
  }
}

/**
 * Class definition of the sale manager.  This class is responsible for maintaining the sale state
 * and provides an interface to perform actions on the sale.  An instance of this class would be
 * instantiated when a sale is in progress.
 */
export default class SaleManager extends BaseObject {
  static get Events() {
    return _Events;
  }

  static get Errors() {
    return _Errors;
  }

  /**
   * Constructor
   *
   * @param {Sale} sale       The Sale model to associate with this manager instance
   * @param {PaymentCollection} payments  A collection of payment service payments associated with the sale
   * @param {Object} options  An object hash of the options passed to the Sale manger.  Possible
   *                          options are:
   * @param {Customer} options.attachedCustomer   The customer attached to the sale
   * @param {SalePaymentCollection} options.refundablePayments    A collection of refundable payments
   * @param {CashRoundingOptions} options.cashRoundingOptions  The cash rounding options
   */
  constructor(sale, payments, options = {}) {
    super(options);

    if (!(sale instanceof Sale)) {
      throw 'A sale must be specified when constructing a SaleManager';
    }

    this._sale = sale;
    this._pendingPayments = {};
    this._servicePayments = payments || new PaymentCollection();
    this._isPaymentCollectionFromService = options.isPaymentCollectionFromService || false;
    this._isSaleUpdating = false;
    this._availableAmountFromCreditLimit = null;
    this._depositBalance = null;

    if (options.attachedCustomer && options.attachedCustomer instanceof Customer) {
      this._attachedCustomer = options.attachedCustomer;
    }

    this._giftCardModule = options.giftCardModule ? options.giftCardModule : false;

    this._depositManager = new DepositManager(this.sale, this.creditAccountID);

    if (options.refundablePayments) {
      this.refundablePayments = options.refundablePayments;
    }

    this.listenTo(this._sale, 'change:displayableTotal', this._onTotalChanged);

    if (Modules.hasModule('register.sales.cash_rounding')) {
      if (options.cashRoundingOptions) {
        this._cashRoundingOptions = options.cashRoundingOptions;
      }

      this.cashRoundingDelta = this.sale.get('cashRoundingDelta') || new Decimal(0);
      this.cashRoundedTotal = this.sale.get('cashRoundedTotal') || new Decimal(0);
      this.cashRoundedBalance = this.sale.get('cashRoundedBalance') || new Decimal(0);
      this.cashRoundedCashMax = new Decimal(0);
      this.cashRoundedChange = new Decimal(0);
      this.cashRoundedCashPayment = new Decimal(0);
    }

    this._calculatePayments();

    // Reconcile invoicing intents on load: upsert active intents and delete orphaned ones
    this._reconcileIntentsOnLoad();
    currentSaleManagerInstance = this;
  }

  /**
   * Reconciles invoicing intents when a sale is loaded.
   * Detects which of the 4 invoicing scenarios are active based on current sale payments,
   * upserts intents for active scenarios, and deletes orphaned intents for inactive ones.
   *
   * Skips archived, completed, and voided sales so cleanup does not call Invoicing after finalize.
   *
   * Runs asynchronously to avoid blocking SaleManager initialization.
   *
   * @private
   */
  _reconcileIntentsOnLoad() {
    if (
      !Modules.hasModule('customers.invoicing_integration.free_tier') ||
      !Modules.hasModule('invoicing.api.v1') ||
      !Modules.hasModule('invoicing.api.v1.cleanup_intents')
    ) {
      return;
    }

    if (!this._attachedCustomer) {
      return;
    }

    const creditAccountID = this._attachedCustomer.get('creditAccountID');
    if (!creditAccountID) {
      return;
    }

    // Skip intent reconciliation for cancelled sales to avoid deleting intents on the wrong sale.
    if (this._sale.get('archived') === true) {
      return;
    }

    // Completed / voided sales should not run client-side orphan cleanup (avoids DELETEs against
    // finalized invoicing transactions and refund / history flows that reload the sale).
    if (this._sale.get('completed') === true || this._sale.get('voided') === true) {
      return;
    }

    (async () => {
      try {
        const saleId = `${this._sale.id}`;
        const scenarios = this._detectIntentScenarios();

        // Upsert refund intent if deposit refund scenario is active
        if (scenarios.hasDepositRefund) {
          await this._upsertRefundIntent(creditAccountID, saleId);
        }

        // Delete orphaned intents for inactive scenarios
        await this._deleteOrphanedIntents(creditAccountID, saleId, scenarios);
      } catch (error) {
        datadogRum.addError(error, {
          message: 'Failed to reconcile intents on sale load',
          saleID: this._sale.id,
          timestamp: new Date().toISOString(),
        });
      }
    })();
  }

  /**
   * Analyzes current sale payments to determine which invoicing scenarios are active.
   *
   * @returns {{ hasOnAccountPayment: boolean, hasDepositRefund: boolean, hasDepositInstruction: boolean, hasDepositUsage: boolean, hasReturnInstruction: boolean, hasInvoicePaymentInstruction: boolean }}
   * @private
   */
  _detectIntentScenarios() {
    const CREDIT_ACCOUNT_PAYMENT_TYPE_CODE = 'SCA';
    const CREDIT_ACCOUNT_PAYMENT_TYPE_ID = 4;
    const activePayments = this._getActivePayments();

    // Find credit account withdrawals (positive amount, type SCA/4)
    const creditAccountWithdrawals = activePayments.filter((payment) => {
      const isCreditAccount = payment.PaymentType?.code
        ? payment.PaymentType.code === CREDIT_ACCOUNT_PAYMENT_TYPE_CODE
        : Number(payment.paymentTypeID) === CREDIT_ACCOUNT_PAYMENT_TYPE_ID;
      return isCreditAccount && this._isPositiveAmount(payment.amount);
    });

    // Find credit card deposit refunds (negative amount, refPaymentID > 0)
    const creditCardDepositRefunds = this._getCreditCardDepositRefunds(activePayments);

    // Determine if deposit refund scenario: matching withdrawals and CC refunds within $0.01
    let hasDepositRefund = false;
    if (creditAccountWithdrawals.length > 0 && creditCardDepositRefunds.length > 0) {
      const totalCreditCardRefunds = creditCardDepositRefunds.reduce((sum, payment) => {
        return sum + Math.abs(parseFloat(payment.amount.toString()));
      }, 0);

      const totalCreditAccountWithdrawals = creditAccountWithdrawals.reduce((sum, payment) => {
        return sum + parseFloat(payment.amount.toString());
      }, 0);

      hasDepositRefund = Math.abs(totalCreditCardRefunds - totalCreditAccountWithdrawals) <= 0.01;
    }

    // On-account payment: credit account withdrawal that is NOT part of a deposit refund
    const hasOnAccountPayment = creditAccountWithdrawals.length > 0 && !hasDepositRefund;

    return {
      hasOnAccountPayment,
      hasDepositRefund,
      hasDepositInstruction: this._shouldUseDepositInstructionOnly(),
      hasDepositUsage: this.hasDepositPayments,
      hasReturnInstruction: this._shouldUseReturnInstructionOnly(),
      hasInvoicePaymentInstruction: this.hasInvoicePayments,
    };
  }

  /**
   * Upserts a refund intent for the current sale (deposit refund scenario).
   *
   * @param {number|string} creditAccountID
   * @param {string} saleId
   * @private
   */
  async _upsertRefundIntent(creditAccountID, saleId) {
    const customerID = this._attachedCustomer.get('customerID');
    const currency = window.merchantos.locale.currency_code;
    const accountId = window.merchantos.account.accountID;
    const registerRef = window.merchantos.register.registerID;

    const shopData = window.merchantos.shop;
    const customerResponse = await CustomerAccountService.getLsCustomerAccount(customerID);
    const customerData = customerResponse.Customer;
    const employeeData = window.merchantos.employee;

    const invoice = {
      customer: buildCustomerFromData(customerData),
      employee: buildEmployeeFromData(employeeData),
      location: buildLocationFromShopData(shopData),
    };

    // Calculate total refund amount from credit card deposit refunds
    const activePayments = this._getActivePayments();
    const creditCardDepositRefunds = this._getCreditCardDepositRefunds(activePayments);

    const totalCreditCardRefunds = creditCardDepositRefunds.reduce((sum, payment) => {
      return sum + Math.abs(parseFloat(payment.amount.toString()));
    }, 0);

    await RefundIntentService.upsertRefundIntent({
      creditAccountId: creditAccountID,
      customerId: `${customerID}`,
      saleId,
      amount: totalCreditCardRefunds,
      currency,
      accountId: `${accountId}`,
      registerRef: `${registerRef}`,
      invoice,
      customerData,
    });
  }

  /**
   * Deletes orphaned intents for scenarios that are NOT active.
   * Each delete is independent and wrapped in its own catch to avoid blocking others.
   *
   * @param {number|string} creditAccountID
   * @param {string} saleId
   * @param {{ hasOnAccountPayment: boolean, hasDepositRefund: boolean, hasDepositInstruction: boolean, hasDepositUsage: boolean, hasReturnInstruction: boolean, hasInvoicePaymentInstruction: boolean }} scenarios
   * @private
   */
  async _deleteOrphanedIntents(creditAccountID, saleId, scenarios) {
    const deletions = [];

    if (!scenarios.hasOnAccountPayment && Modules.hasModule('invoicing.api.v1')) {
      deletions.push(InvoiceService.deleteInvoiceIntent(creditAccountID, saleId).catch(() => {}));
    }

    if (!scenarios.hasDepositRefund && Modules.hasModule('invoicing.api.v1.refund_intent')) {
      deletions.push(
        RefundIntentService.deleteRefundIntent(creditAccountID, saleId).catch(() => {})
      );
    }

    if (
      !scenarios.hasDepositInstruction &&
      Modules.hasModule('invoicing.api.v1.deposit_instructions')
    ) {
      deletions.push(
        DepositInstructionService.deleteDepositInstruction(creditAccountID, saleId).catch(() => {})
      );
    }

    if (!scenarios.hasDepositUsage && Modules.hasModule('invoicing.api.v1.consume_deposit')) {
      deletions.push(
        DepositUsageIntentService.deleteDepositUsageIntent(creditAccountID, saleId).catch(() => {})
      );
    }

    if (
      !scenarios.hasReturnInstruction &&
      Modules.hasModule('invoicing.api.v1.return_instructions')
    ) {
      deletions.push(
        ReturnInstructionService.deleteReturnInstruction(creditAccountID, saleId).catch(() => {})
      );
    }

    if (
      !scenarios.hasInvoicePaymentInstruction &&
      Modules.hasModule('invoicing.api.v1.payment_instructions')
    ) {
      deletions.push(
        InvoicePaymentInstructionService.deleteInvoicePaymentInstruction(
          creditAccountID,
          saleId
        ).catch(() => {})
      );
    }

    await Promise.allSettled(deletions);
  }

  /**
   * Checks if an amount is positive, handling Decimal objects and primitive values.
   *
   * @param {*} amount
   * @returns {boolean}
   * @private
   */
  _getActivePayments() {
    const allPayments = this._sale.get('SalePayments')?.SalePayment || [];
    return allPayments.filter((payment) => !payment.archived);
  }

  /**
   * Filters active payments to find credit card deposit refunds.
   * These are negative-amount payments with a refPaymentID, excluding credit account payments.
   *
   * @param {Array} activePayments
   * @returns {Array}
   * @private
   */
  _getCreditCardDepositRefunds(activePayments) {
    const CREDIT_ACCOUNT_PAYMENT_TYPE_CODE = 'SCA';
    const CREDIT_ACCOUNT_PAYMENT_TYPE_ID = 4;
    return activePayments.filter((payment) => {
      const hasRefPaymentID = payment.refPaymentID > 0;
      const hasPaymentID = !!payment.paymentID;
      const hasAmount = !!payment.amount;
      const isNotCreditAccount = payment.PaymentType?.code
        ? payment.PaymentType.code !== CREDIT_ACCOUNT_PAYMENT_TYPE_CODE
        : Number(payment.paymentTypeID) !== CREDIT_ACCOUNT_PAYMENT_TYPE_ID;
      return (
        hasRefPaymentID &&
        hasPaymentID &&
        isNotCreditAccount &&
        hasAmount &&
        this._isNegativeAmount(payment.amount)
      );
    });
  }

  _isPositiveAmount(amount) {
    if (!amount) return false;
    if (typeof amount.isNegative === 'function') return !amount.isNegative();
    if (typeof amount.s !== 'undefined') return amount.s === 1;
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount > 0;
  }

  /**
   * Checks if an amount is negative, handling Decimal objects and primitive values.
   *
   * @param {*} amount
   * @returns {boolean}
   * @private
   */
  _isNegativeAmount(amount) {
    if (!amount) return false;
    if (typeof amount.isNegative === 'function') return amount.isNegative();
    if (typeof amount.s !== 'undefined') return amount.s === -1;
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return numAmount < 0;
  }

  /**
   * Constructs a SaleManager using an existing sale ID.  Retrieves the model using the API
   * and injects the result into the construction of a new SaleManager.  The SaleManager instance
   * is returned with the resolved promise.
   *
   * @param {Number} saleID       The sale ID to continue
   * @returns {jQuery.Promise}    Resolves hen the a sale has been successfully retrieved.
   *                              Resolves with a new SaleManager instance.
   */
  static continueSale(saleID) {
    const deferred = $.Deferred();
    const servicePaymentDeferred = $.Deferred();
    const filter = new Filter().loadRelations('all');
    const servicePayments = new PaymentCollection();
    const paymentFilter = new Filter().exactly('saleID', saleID);
    const sale = new Sale({ saleID });
    const options = {};
    const supportMessage = `Try again and if the problem persists, contact <a class="strong-link" target="_blank" href="https://www.lightspeedhq.com/support/retail">Support</a>`;

    // We make an attempt to fetch the refundable payment list
    // from the pay-service
    servicePayments
      .fetch(paymentFilter)
      .done(() => {
        options.isPaymentCollectionFromService = true;
        servicePaymentDeferred.resolve();
      })
      .fail((error) => {
        // TODO: Remove this message when the health check banner will be present in CLD-30509.
        if (error) {
          const { message } = error.responseJSON;
          window.alertUser(message, 300);
        }
        options.isPaymentCollectionFromService = false;
        servicePaymentDeferred.reject();
      });

    // Regardless of the API's availability to return the list
    // of refundable payment, we will still display the non-integrated
    // payments options. A merchant can still finish the sale using
    // cash or any other non-integrated payment method.
    $.when(servicePaymentDeferred).always(() => {
      sale
        .fetch(filter)
        .done(() => {
          let customer = null;
          let customerDeferred = $.Deferred();
          const refundPaymentsDeferred = $.Deferred();
          const giftCardModuleDeferred = $.Deferred();
          const cashRoundingOptionsDeferred = $.Deferred();

          // Fetch Customer information
          if (sale.get('customerID') > 0) {
            customer = new Customer({ customerID: sale.get('customerID') });
            customerDeferred = customer.fetch(filter);
          } else {
            customerDeferred.resolve();
          }

          // Fetch Refundable Payments
          SaleManager.fetchRefundablePayments(sale)
            .done((refundablePayments) => {
              refundPaymentsDeferred.resolve(refundablePayments);
            })
            .fail((fetchRefundablePaymentFailure) => {
              const reason = get(fetchRefundablePaymentFailure, 'responseJSON.message', null);
              refundPaymentsDeferred.reject({
                message: `Unable to get any refundable payments from the payment-service`,
                reason,
              });
            });

          if (Modules.hasModule('register.sales.cash_rounding')) {
            fetchCashRoundingOptions()
              .then((result) => cashRoundingOptionsDeferred.resolve(result))
              .catch(() => cashRoundingOptionsDeferred.resolve());
          } else {
            cashRoundingOptionsDeferred.resolve();
          }

          // Get gift card module
          window.merchantos.session
            .getModule(_GIFT_CARD_MODULE_NAME)
            .done((giftCardModule) => {
              giftCardModuleDeferred.resolve(giftCardModule);
            })
            .fail((getModuleGiftCardFailure) => {
              const reason = get(getModuleGiftCardFailure, 'responseJSON.message', null);

              giftCardModuleDeferred.reject({
                message: `Unable to use the gift card at the moment`,
                reason,
              });
            });

          $.when(
            refundPaymentsDeferred,
            giftCardModuleDeferred,
            customerDeferred,
            cashRoundingOptionsDeferred
          )
            .fail(({ message, reason }) => {
              Monitoring.addError(new Error(message), 'SaleManager', { saleID, reason });
            })
            .always((refundablePayments, giftCardModule, _, cashRoundingOptions) => {
              options.attachedCustomer = customer;
              options.refundablePayments = refundablePayments;
              options.giftCardModule = giftCardModule;
              if (Modules.hasModule('register.sales.cash_rounding') && cashRoundingOptions) {
                options.cashRoundingOptions = cashRoundingOptions;
              }

              deferred.resolve(new SaleManager(sale, servicePayments, options));
            });
        })
        .fail((salePaymentFailure) => {
          const message = `Unable to get the sale information to initialize the Sale Manager`;
          const reason = get(salePaymentFailure, 'responseJSON.message', null);

          Monitoring.addError(new Error(message), 'SaleManager', { saleID, reason });

          deferred.reject(
            `Payment processing capabilities are temporarily unavailable. ${supportMessage}`
          );
        });
    });

    return deferred.promise();
  }

  /**
   * Constructs a SaleManager by refunding an existing sale.  Retrieves the model using the API
   * and injects the result into the construction of a new SaleManager.  The SaleManager instance
   * is returned with the resolved promise.
   *
   * @param {Number} saleID       The sale ID to continue
   * @returns {jQuery.Promise}    Resolves when a refund sale has been successfully created.
   *                              Resolves with a new SaleManager instance.
   */
  static refundSale(saleID) {
    let filter = new Filter().loadRelations('all');
    let deferred = $.Deferred();
    let sale = new Sale({ saleID });

    sale
      .refund(filter)
      .done(() => {
        deferred.resolve(new SaleManager(sale));
      })
      .fail(() => {
        window.console.error('Could not create a SaleManager to refund the sale for:', saleID);
        deferred.reject();
      });

    return deferred.promise();
  }

  /**
   * Retrieves refundable payments from a prior sale.
   *
   * @param {Sale} sale   The sale to fetch the refundable payments
   * @returns {jQuery.Promise}  Resolves with a PaymentList when the refundable payments has been retrieved
   */
  static fetchRefundablePayments(sale) {
    let deferred = $.Deferred();

    if (sale && sale instanceof Sale) {
      let saleLines = sale.get('SaleLines');

      if (
        saleLines &&
        saleLines.SaleLine &&
        saleLines.SaleLine.length &&
        saleLines.SaleLine[0].parentSaleLineID > 0
      ) {
        // find a parent sale line so we can figure out which sale the payments are
        // attached to
        let parentSaleLine = new SaleLine({
          saleLineID: saleLines.SaleLine[0].parentSaleLineID,
        });

        parentSaleLine
          .fetch()
          .done(() => {
            // now fetch the payments associated with the original sale
            let filter = new Filter()
              .exactly('saleID', parentSaleLine.get('saleID'))
              .loadRelations('all');
            let refundableSalePayments = new SalePaymentCollection();
            let refundableServicePayments = new PaymentCollection();
            $.when(refundableSalePayments.fetch(filter), refundableServicePayments.fetch(filter))
              .done(() => {
                deferred.resolve(
                  PaymentList.fromPayments(refundableSalePayments, refundableServicePayments)
                );
              })
              .fail((errorFromPromises) => {
                deferred.reject(errorFromPromises);
              });
          })
          .fail((errorFromFetching) => {
            deferred.reject(errorFromFetching);
          });
      } else {
        deferred.resolve(new PaymentList());
      }
    } else {
      deferred.reject(`Cannot fetch refundable payments for the sale: ${sale}`);
    }

    return deferred.promise();
  }

  /**
   * Return if the sale is getting updated
   * by an external source (e.g.: Ajax call)
   */
  get isSaleUpdating() {
    return this._isSaleUpdating;
  }

  /**
   * Set this flag to determine if the sale is getting
   * updated by an external source (e.g.: Ajax call)
   */
  set isSaleUpdating(status) {
    if (typeof status !== 'boolean') {
      throw new Error(`_isSaleUpdating only accepts boolean you provided :: ${typeof status}`);
    }

    this._isSaleUpdating = status;
    return this._isSaleUpdating;
  }

  /**
   * Retrieves any pending credit/debit transactions in the sale.  This can happen if the
   * payment flow was interrupted in some fashion (e.g. browser was closed during a transaction).
   */
  get pendingCreditDebitTransaction() {
    const pendingTransaction = this._servicePayments.findWhere({
      status: PaymentModel.Status.PENDING,
    });

    return pendingTransaction;
  }

  /**
   * Getter for the Sale model
   *
   * @returns {Sale}  The Sale model managed by this instance of the SaleManager
   */
  get sale() {
    return this._sale;
  }

  /**
   * Getter for retrieving the deposit manager
   *
   * @returns {DepositManager}
   */
  get deposits() {
    return this._depositManager;
  }

  /**
   * Getter for retrieving whether or not we have the gift card module
   *
   * @returns {DepositManager}
   */
  get giftCardModule() {
    return this._giftCardModule;
  }

  /**
   * Getter for the Customer model
   *
   * @returns {Customer}  The customer associated with this sale managed  by
   *                      this instance of the SaleManager
   */
  get customer() {
    return this._attachedCustomer;
  }

  /**
   * Getter for the total including pending payments
   *
   * @returns {Decimal}
   */
  get pendingPaymentsTotal() {
    return this._totalPayments;
  }

  /**
   * Setter for the total including pending payments
   *
   * @param {*} total The new total including pending payments
   */
  set pendingPaymentsTotal(total) {
    this._totalPayments = new Decimal(total); // safe decimal precision
    this.trigger(_Events.PAYMENT_TOTAL_CHANGED, this._totalPayments);
  }

  get cashRoundedTotal() {
    return this._cashRoundedTotal;
  }
  set cashRoundedTotal(cashRoundedTotal) {
    this._cashRoundedTotal = new Decimal(cashRoundedTotal);
  }

  get cashRoundingDelta() {
    return this._cashRoundingDelta;
  }
  set cashRoundingDelta(cashRoundingDelta) {
    this._cashRoundingDelta = new Decimal(cashRoundingDelta);
  }

  get cashRoundedBalance() {
    return this._cashRoundedBalance;
  }

  set cashRoundedBalance(cashRoundedBalance) {
    this._cashRoundedBalance = new Decimal(cashRoundedBalance);
  }

  get cashRoundedCashMax() {
    return this._cashRoundedCashMax;
  }

  set cashRoundedCashMax(cashRoundedCashMax) {
    this._cashRoundedCashMax = new Decimal(cashRoundedCashMax);
  }

  get cashRoundedChange() {
    return this._cashRoundedChange;
  }

  set cashRoundedChange(cashRoundedChange) {
    this._cashRoundedChange = new Decimal(cashRoundedChange);
  }

  get cashRoundedCashPayment() {
    return this._cashRoundedCashPayment;
  }

  set cashRoundedCashPayment(cashRoundedCashPayment) {
    this._cashRoundedCashPayment = new Decimal(cashRoundedCashPayment);
  }

  get cashRoundingOptions() {
    return this._cashRoundingOptions;
  }

  get isCashRoundingActive() {
    return !!this.cashRoundingOptions?.cashRoundingEnabled;
  }

  get isBalanceSatisfiedWithCashRounding() {
    return (
      this.pendingBalance.isEqualTo(0) ||
      (!this.pendingBalance.isEqualTo(0) &&
        !this.cashPayment.isZero() &&
        this.cashRoundedBalance.isEqualTo(0))
    );
  }
  /**
   * Getter for the calcTips
   *
   * @returns {Decimal} calcTips
   */
  get calcTips() {
    return this._calcTips;
  }

  /**
   * Getter for the calcSurcharges
   *
   * @returns {Decimal} calcSurcharges
   */
  get calcSurcharges() {
    return this._calcSurcharges;
  }

  /**
   * Setter for the calcTips
   *
   * @param {Decimal} calcTips The calcTips
   */
  set calcTips(calcTips) {
    this._calcTips = new Decimal(calcTips);
    this.trigger(_Events.CALCTIPS_CHANGED, this._calcTips);
  }

  /**
   * Setter for the calcSurcharges
   *
   * @param {Decimal} calcSurcharges The calcSurcharges
   */
  set calcSurcharges(calcSurcharges) {
    this._calcSurcharges = new Decimal(calcSurcharges);
    this.trigger(_Events.CALCSURCHARGES_CHANGED, this._calcSurcharges);
  }

  /**
   * Getter for the balance including pending payments
   *
   * @returns {Decimal}
   */
  get pendingBalance() {
    return this._pendingBalance;
  }

  /**
   * Setter for the balance including pending payments
   *
   * @param {*} pendingBalance The new balance including pending payments
   */
  set pendingBalance(pendingBalance) {
    this._pendingBalance = new Decimal(pendingBalance); // safe decimal precision
    this.trigger(_Events.BALANCE_CHANGED, this._pendingBalance);
  }

  /**
   * Getter for the change due including pending payments
   *
   * @returns {Decimal}
   */
  get pendingChange() {
    return this._pendingChange;
  }

  /**
   *Setter for the change due including pending payments
   *
   * @param {*} pendingChange The new change due including pending payments
   */
  set pendingChange(pendingChange) {
    this._pendingChange = new Decimal(pendingChange); // safe decimal precision
    this.trigger(_Events.CHANGE_DUE_CHANGED, this._pendingChange);
  }

  /**
   * Getter for the total amount of cash payments
   *
   * @returns {*}
   */
  get cashPayment() {
    return this._cashPayment;
  }

  /**
   * Setter for the total amount of cash payments
   *
   * @param {*} total The new cash payments total
   */
  set cashPayment(total) {
    this._cashPayment = new Decimal(total);
    this.trigger(_Events.CASH_CHANGED, this._cashPayment);
  }

  /**
   * Getter for the managed sale's current total
   *
   * @returns {Decimal}
   */
  get saleTotal() {
    let saleTotal = new Decimal(0);
    if (this.sale.has('displayableTotal')) {
      // BigNumber.js will no longer throw an exception when not getting a number
      // or a Decimal. The following situation would return a Decimal with {c:null, e:null, s:0}
      saleTotal = new Decimal(this.sale.get('displayableTotal'));

      // Still in case our API does it, this would have a dramatic effect on our code.
      // Hence, we throw an unhandled exception to be caught by the monitoring
      // if the contract ever change.
      if (saleTotal.isNaN()) {
        throw new Error(`The "displayableTotal" return by the API is not a number or decimal.`);
      }
    }

    if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return saleTotal.plus(this.totalInvoicePaymentAmount);
    }

    return saleTotal;
  }

  /**
   * Getter for the currently available credit balance, if any, for this sale.
   * @returns {Decimal|null} - The decimal available balance, or null.
   */
  get availableCreditBalance() {
    let balance = null;
    if (this.customer) {
      if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
        balance =
          this._availableAmountFromCreditLimit != null
            ? new Decimal(this._availableAmountFromCreditLimit)
            : this._availableAmountFromCreditLimit;
      } else {
        let account = this.customer.getRelation('CreditAccount', CreditAccount);
        if (account) {
          balance = account.availableCreditBalance;
        }
      }
    }

    if (Modules.hasModule('register.payment.refundable_credit_payments')) {
      return balance ? balance.minus(this.totalDepositRefunds) : null;
    }

    return balance;
  }

  /**
   * Getter for the currently available deposit balance, if any, for this sale.
   * @returns {Decimal|null} - The decimal available balance, or null.
   */
  async fetchInvoicingCreditAccountBalances() {
    if (this.sale.get('customerID') && this.creditAccountID) {
      try {
        const creditAccount = this.customer.getRelation('CreditAccount', CreditAccount);
        return await CustomerAccountService.getCustomerAccount(
          `${this.creditAccountID}`,
          `${creditAccount.attributes.creditLimit ?? ''}`
        );
      } catch (e) {
        return null; // fallback value;
      }
    }
    return null;
  }

  /**
   * Getter for the total deposit refunds, if any, for this sale.
   * @returns {Decimal} - The decimal available balance, or null.
   */
  get totalDepositRefunds() {
    let depositRefundBalance = new Decimal(0);
    let depositRefunds = this.deposits.depositRefunds;
    _.each(depositRefunds, (depositRefund) => {
      let amount = new Decimal(depositRefund.depositPayment.amount.abs().toNumber());
      depositRefundBalance = depositRefundBalance.plus(amount);
    });

    return depositRefundBalance;
  }

  /**
   * Getter for the credit balance for the customer.  The credit balance would be
   * the value of the credit that the customer has on deposit and in store credit.
   *
   * @returns {Decimal}   The amount of in-store credit and deposits the customer
   *                      has on hand to make payments (does NOT include the customer's
   *                      credit limit).
   */
  get creditAccountBalance() {
    let balance = new Decimal(0);
    if (this.customer) {
      let account = this.customer.getRelation('CreditAccount', CreditAccount);
      if (account) {
        // Any available credit balance is expressed as a negative value in the API.
        // A positive credit balance indicates that the customer owes the store money.
        balance = account.get('balance').negated();
      }
    }
    return balance;
  }

  /**
   * Getter for the creditAccountID associated with this sale, if any.
   *
   * @returns {Number|null} - The credit account id.  Or null.
   */
  get creditAccountID() {
    let creditAccountID = null;
    if (this.customer && this.customer.get('creditAccountID') > 0) {
      creditAccountID = this.customer.get('creditAccountID');
    }
    return creditAccountID;
  }

  /**
   * Getter for retrieving credit/debit payments from pending payments and the sale.
   *
   * @returns {SalePaymentCollection} A collection of credit/debit sale payments.
   */
  get creditDebitPayments() {
    if (Modules.hasModule('register.payment.refundable_credit_payments')) {
      return this.filterAndEnhancePayments(PaymentType.Types.CREDIT_CARD);
    }

    let creditDebitPayments = this.getPaymentsOfType(PaymentType.Types.CREDIT_CARD);
    const status = [PaymentModel.Status.APPROVED, PaymentModel.Status.PRE_AUTHORIZED];
    if (
      this.paymentProvider &&
      get(this.paymentProvider, 'activeGateway.attributes.parameters.platformVersion', '') ===
        GatewayConfig.PLATFORM_VERSIONS.v3
    )
      status.push(PaymentModel.Status.PENDING);

    if (this._servicePayments) {
      creditDebitPayments = new SalePaymentCollection(
        creditDebitPayments.filter((salePayment) => {
          let paymentServicePayment = this._servicePayments.findWhere({
            paymentID: salePayment.get('paymentID'),
          });

          return paymentServicePayment
            ? !salePayment.get('archived') &&
                status.includes(paymentServicePayment.get('status')) &&
                paymentServicePayment.get('operationType') !== PaymentModel.OperationType.UNKNOWN &&
                !this.isRefundOfPaymentInSale(paymentServicePayment)
            : true;
        })
      );

      // Add the payment service transaction details to the returned payments.  This is
      // for the listing view so like for pending payments this will be deprecated when/if
      // we use a custom listing view to list the payments.
      creditDebitPayments.forEach((salePayment) => {
        let paymentServicePayment = this._servicePayments.findWhere({
          paymentID: salePayment.get('paymentID'),
        });

        if (paymentServicePayment) {
          // create a payment to obtain the payment details
          const payment = PaymentFactory.createPaymentFromModel(salePayment, paymentServicePayment);

          salePayment.details = payment.details;
          salePayment.details.operationType = paymentServicePayment.get('operationType');
        } else if (salePayment.has('CCCharge')) {
          let payment = PaymentFactory.createPaymentFromModel(salePayment);
          salePayment.details = payment.details;
        }
      });
    }

    return creditDebitPayments;
  }

  /**
   * Getter for retrieving credit account payments on a sale by the customer.
   *
   * @returns {SalePaymentCollection} A collection of credit account payments on a sale by the customer.
   */
  get creditAccountPayments() {
    return this.filterAndEnhancePayments(PaymentType.Types.CREDIT_ACCOUNT);
  }

  /**
   * Helper method to filter and enhance payment details based on the payment type.
   *
   * @param {string} paymentType The type of payments to retrieve.
   * @returns {SalePaymentCollection} A collection of filtered and enhanced payments.
   */
  filterAndEnhancePayments(paymentType) {
    let payments = this.getPaymentsOfType(paymentType);
    const status = [PaymentModel.Status.APPROVED, PaymentModel.Status.PRE_AUTHORIZED];

    if (
      this.paymentProvider &&
      get(this.paymentProvider, 'activeGateway.attributes.parameters.platformVersion', '') ===
        GatewayConfig.PLATFORM_VERSIONS.v3
    ) {
      status.push(PaymentModel.Status.PENDING);
    }

    if (this._servicePayments) {
      payments = new SalePaymentCollection(
        payments.filter((salePayment) => {
          let paymentServicePayment = this._servicePayments.findWhere({
            paymentID: salePayment.get('paymentID'),
          });

          return paymentServicePayment
            ? !salePayment.get('archived') &&
                status.includes(paymentServicePayment.get('status')) &&
                paymentServicePayment.get('operationType') !== PaymentModel.OperationType.UNKNOWN &&
                !this.isRefundOfPaymentInSale(paymentServicePayment)
            : true;
        })
      );

      // Add the payment service transaction details to the returned payments.  This is
      // for the listing view so like for pending payments this will be deprecated when/if
      // we use a custom listing view to list the payments.
      payments.forEach((salePayment) => {
        let paymentServicePayment = this._servicePayments.findWhere({
          paymentID: salePayment.get('paymentID'),
        });

        if (paymentServicePayment) {
          const payment = PaymentFactory.createPaymentFromModel(salePayment, paymentServicePayment);
          salePayment.details = payment.details;
          salePayment.details.operationType = paymentServicePayment.get('operationType');
        } else if (salePayment.has('CCCharge')) {
          let payment = PaymentFactory.createPaymentFromModel(salePayment);
          salePayment.details = payment.details;
        }
      });
    }

    return payments;
  }

  /**
   * Returns the total amount of credit payments currently in the sale.
   *
   * @returns {Decimal}   The total amount of credit payments in the sale.
   */
  get totalCreditPayments() {
    let creditPaymentAmount = new Decimal(0);
    const creditPayments = this.creditDebitPayments;

    creditPayments.forEach((payment) => {
      creditPaymentAmount = creditPaymentAmount.plus(payment.get('amount'));
    });

    return creditPaymentAmount;
  }

  /**
   * Returns the total amount of credit payments currently in the sale.
   *
   * @returns {Decimal}   The total amount of credit payments in the sale.
   */
  get totalCreditAccountPayments() {
    let creditPaymentAmount = new Decimal(0);
    const creditPayments = this.creditAccountPayments;

    creditPayments.forEach((payment) => {
      creditPaymentAmount = creditPaymentAmount.plus(payment.get('amount'));
    });

    return creditPaymentAmount;
  }

  /**
   * Returns tipEnabled from sale.
   * @returns {Boolean} TipEnabled from sale
   */
  get isTipEnabled() {
    if (this.sale.has('tipEnabled')) {
      return this.sale.get('tipEnabled');
    }
    return false;
  }

  /**
   * Returns surchargeEnabled from sale.
   * @returns {Boolean} SurchargeEnabled from sale
   */
  get isSurchargeEnabled() {
    if (this.sale.has('surchargeEnabled')) {
      return this.sale.get('surchargeEnabled');
    }
    return false;
  }

  /**
   * Retrieves a service payment from the sale manager
   *
   * @param {string} paymentID    The payment ID
   * @returns {*|Backbone.Model}
   */
  getServicePayment(paymentID) {
    return this._servicePayments.findWhere({
      paymentID,
    });
  }

  /**
   * Retreives a service payment containing a transaction with the specified gateway reference.
   *
   * @param {string} gatewayReference     The reference number used by the gateway to identify a
   *                                      transaction
   * @returns {Payment|null}
   */
  getServicePaymentByGatewayRef(gatewayReference) {
    let servicePayment = null;
    this._servicePayments.forEach((payment) => {
      if (payment.paymentTransaction.get('gatewayRecordRef') === gatewayReference) {
        servicePayment = payment;
      }
    });

    return servicePayment;
  }

  /**
   * Getter for retrieving gift card payments from pending payments and the sale.
   *
   * @returns {SalePaymentCollection} A collection of gift card sale payments.
   */
  get giftCardPayments() {
    let giftCardPayments = this.getPaymentsOfType(PaymentType.Types.GIFT_CARD);

    // When we create a gift card a payment will be created in the sale attached to
    // a sale account.  Just ignore it since we don't want to account for it in the
    // sale.
    giftCardPayments = new SalePaymentCollection(
      giftCardPayments.filter((salePayment) => !salePayment.has('SaleAccounts'))
    );

    return giftCardPayments;
  }

  /**
   * Retrieves the total of unarchived payments already attached to the sale.  It also
   * excludes any special payments added to the sale for the specific case where there
   * is a gift card purchase in the sale or a credit account deposit.
   * Note, Deposit Refund payments are always accompanied by an equivalent negative payments
   * which are expected to balance in the sale's total.
   *
   * @returns {Decimal}   The total contribution of payments currently in the sale
   */
  get totalInSalePayments() {
    let totalPayments = new Decimal('0.0');

    let salePayments =
      this.sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

    salePayments.forEach((salePayment) => {
      const salePaymentType = salePayment.getRelation('PaymentType', PaymentType);
      if (
        !salePayment.get('archived') &&
        !salePayment.has('SaleAccounts') &&
        // This is a credit account Deposit and should not be considered as a payment.
        !(
          salePaymentType &&
          salePaymentType.get('type') === PaymentType.Types.CREDIT_ACCOUNT &&
          salePayment.get('amount') < 0
        )
      ) {
        let servicePayment = this._servicePayments.findWhere({
          paymentID: salePayment.get('paymentID'),
        });

        if (servicePayment) {
          // if there's a service payment we need to make sure it's approved/pre-authorized and not refunded
          const isApprovedOrPreAuth =
            servicePayment.get('status') === PaymentModel.Status.APPROVED ||
            (Modules.hasModule('payments.pre_auth') &&
              servicePayment.get('status') === PaymentModel.Status.PRE_AUTHORIZED);

          if (
            isApprovedOrPreAuth &&
            servicePayment.get('operationType') !== PaymentModel.OperationType.UNKNOWN &&
            !this.isRefundOfPaymentInSale(servicePayment)
          ) {
            totalPayments = totalPayments.plus(salePayment.get('amount'));
          }
        } else {
          totalPayments = totalPayments.plus(salePayment.get('amount'));
        }
      }
    });

    return totalPayments;
  }

  get totalInSaleCashPayments() {
    let totalCashPayments = new Decimal('0.0');

    let salePayments =
      this.sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

    salePayments.forEach((salePayment) => {
      const salePaymentType = salePayment.getRelation('PaymentType', PaymentType);
      if (salePaymentType && salePaymentType.get('type') === PaymentType.Types.CASH) {
        totalCashPayments = totalCashPayments.plus(salePayment.get('amount'));
      }
    });
    return totalCashPayments;
  }

  /**
   * Sets the refundable payments associated with a sale.
   *
   * @param payments {PaymentList}  The payments that may be refunded
   */
  set refundablePayments(payments) {
    if (payments instanceof PaymentList) {
      this._refundablePayments = payments;
    }
  }

  /**
   * Retrieves the refundable payments associated with a sale.
   *
   * @returns {PaymentList}   The payments that may be refunded
   */
  get refundablePayments() {
    return this._refundablePayments || new PaymentList();
  }

  /**
   * Retrieves a list of Payments that may be refunded.  The refundable payments are populated
   * when the SaleManager is created (via a static factory method) or when the sale is refreshed
   * from the SaleManager (refreshSale).
   *
   * @returns {PaymentList}     A list of refundable payments
   */
  get refundablePaymentList() {
    let refundablePayments = new PaymentList();

    if (this.refundablePayments) {
      refundablePayments = this.refundablePayments.filter(
        (payment) => !payment.model.get('archived')
      );
    }

    return refundablePayments;
  }

  /**
   * Retrieves a list of Pending Payments on the refund.
   *
   * @returns {Array.Payment}     An array of pending payments
   */
  get pendingPayments() {
    return this._pendingPayments;
  }

  get invoicePaymentOptions() {
    return this._invoicePaymentOptions;
  }

  set invoicePaymentOptions(invoicePaymentOptions) {
    this._invoicePaymentOptions = invoicePaymentOptions;
    this.refreshSale();
  }

  get totalInvoicePaymentAmount() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return 0;
    }

    return (
      this._invoicePaymentOptions?.invoicesToPay
        ?.reduce((total, payment) => total.plus(Number(payment.amount)), new Decimal(0))
        .toNumber() ?? 0
    );
  }

  get hasInvoicePayments() {
    if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return this._invoicePaymentOptions?.invoicesToPay?.length > 0;
    }

    return false;
  }

  get depositPaymentOptions() {
    return this._depositPaymentOptions;
  }

  set depositPaymentOptions(depositPaymentOptions) {
    this._depositPaymentOptions = depositPaymentOptions;
    this.refreshSale();
    this.trigger(_Events.DEPOSIT_PAYMENT_CHANGED);
  }

  get totalDepositPaymentAmount() {
    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return 0;
    }

    return (
      this._depositPaymentOptions?.depositPayments
        ?.reduce((total, payment) => total.plus(Number(payment.amount)), new Decimal(0))
        .toNumber() ?? 0
    );
  }

  get hasDepositPayments() {
    if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
      return this._depositPaymentOptions?.depositPayments?.length > 0;
    }

    return false;
  }

  get hasInvoiceIntent() {
    if (
      Modules.hasModule('customers.invoicing_integration.free_tier') &&
      Modules.hasModule('invoicing.api.v1')
    ) {
      const hasPendingCreditAccountPayment =
        GetPendingCreditAccountPaymentAmount(this.pendingPayments) > 0;
      const hasDepositRefunds = !this.deposits.refundTotal.isZero();

      // Exclude deposit refund scenarios - credit account payments in those cases
      // are for balancing the refund, not for invoice intent
      return hasPendingCreditAccountPayment && !hasDepositRefunds;
    }

    return false;
  }

  get hasRefundIntent() {
    if (
      Modules.hasModule('customers.invoicing_integration.free_tier') &&
      Modules.hasModule('invoicing.api.v1') &&
      Modules.hasModule('invoicing.api.v1.refund_intent')
    ) {
      return !this.deposits.refundTotal.isZero();
    }

    return false;
  }

  /**
   * Syncs the sale manager model with the API
   *
   * @returns {*}
   */
  refreshSale() {
    let deferred = $.Deferred();
    window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHING));

    if (this.sale) {
      const previousCreditAccountID = this.creditAccountID;
      let filter = new Filter().loadRelations('all');
      let paymentFilter = new Filter().exactly('saleID', this.sale.id);

      // Data like sale lines can either be present or not
      // which causes issues with how Backbone likes to operate.
      // To prevent stale data from being PUT to the server make
      // sure we clean the model before refreshing it (but don't
      // notify any listeners)

      // need to preserve the id because it is cleared as well
      let id = this.sale.get(this.sale.idAttribute);
      this.sale.clear({ silent: true });
      this.sale.set(this.sale.idAttribute, id);

      $.when(this.sale.fetch(filter), this._servicePayments.fetch(paymentFilter))
        .done(() => {
          let customerDeferred = $.Deferred();
          let customer = null;
          if (this.sale.get('customerID')) {
            customer = new Customer({ customerID: this.sale.get('customerID') });
            customerDeferred = customer.fetch(filter);
          } else {
            customerDeferred.resolve();
          }

          $.when(customerDeferred)
            .done(() => {
              this._attachedCustomer = customer;

              const currentCreditAccountID = this.creditAccountID;
              if (
                Modules.hasModule('customers.invoicing_integration.free_tier') &&
                Modules.hasModule('invoicing.api.v1') &&
                previousCreditAccountID &&
                currentCreditAccountID &&
                `${previousCreditAccountID}` !== `${currentCreditAccountID}`
              ) {
                // Attached credit account changed for this sale; clear invoicing-related
                // client state so we don't reuse intents/instructions across accounts.
                this._invoicePaymentOptions = undefined;
                this._depositPaymentOptions = undefined;
              }

              const originalCheck = this.saleTotal.isZero() && !this.deposits.hasDeposits;
              if (
                Modules.hasModule('customers.invoicing_integration.free_tier')
                  ? originalCheck && !this.hasInvoicePayments
                  : originalCheck
              ) {
                // Any existing pending payments should be discarded
                // for missing a zero total sale
                this._pendingPayments = {};
              }

              this.deposits.setInSaleDepositRefunds(this.sale);

              this._calculatePayments();
              SaleManager.fetchRefundablePayments(this.sale)
                .done((refundablePayments) => {
                  this.refundablePayments = refundablePayments;
                  this.trigger(_Events.SALE_REFRESHED);
                  window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHED));

                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              window.console.error('There was an error while attempting to retrieve the sale.');
              deferred.reject();
            });
        })
        .fail(() => {
          window.console.error('There was an error while attempting to retrieve the sale.');
          deferred.reject();
        });
    } else {
      window.dispatchEvent(new CustomEvent(_Events.SALE_REFRESHED));
      deferred.resolve();
    }

    return deferred.promise();
  }

  /**
   * Finishes a sale.  Any pending payments are added to the sale before finishing the
   * sale.  This is a convenience method for updateSale({ completed: true }).
   *
   * @returns {jQuery.Promise}    A jQuery promise that resolves once the sale has been
   *                              successfully finished.
   */
  finishSale() {
    this.trigger(_Events.COMPLETE_SALE_STARTED);
    return this.updateSale({
      complete: true,
    });
  }

  /**
   * Updates the API sale data with the latest sale data including any pending payments or deposits.  If
   * the completed option is set to true, then the sale will be validated before any update is attempted
   * with the server.
   *
   * @param {Object} options  Options used to configure the update request
   * @param {string} options.complete     When true, the sale data will be validated before the request is
   *                                      made and, if valid, will attempt to complete the sale with the API.
   * @returns {*}
   */
  updateSale(options = {}) {
    let sale = new Sale();
    let deferred = $.Deferred();

    let status = this._canFinishSale();

    // We always allow the sale update to happen if we're not trying to complete the sale even
    // if validation fails.  The reason for this is to allow for manual credit card payments
    // where we'll have an out of balance sale until the credit card payment is taken.
    if (status.error === _Errors.NO_ERROR || !options.complete) {
      // create a clone of the managed sale
      sale.set('saleID', this._sale.get('saleID'));
      sale.set(_.clone(this._sale.attributes));

      // indicate to the API that the sale should be finished
      sale.set('completed', options.complete);

      // ignore any pending payments when there's nothing to pay or refund
      // add pending payments to the sale
      let salePayments =
        sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

      _.each(this._pendingPayments, (payment) => {
        // Add pending payments to the sale when the sale total is not zero, we have deposits
        // the allow_zero_sale_total module is enabled, or when we're not trying to complete the sale.
        // The last case lets us blindly add payments to the sale without worrying about validation since
        // the validation in the API only occurs when we try to complete the sale.  This is necessary to
        // allow for manual credit card payments where we'll be in a situation where the sale needs to be
        // out of balance until the manual credit card payment balances it.

        if (
          Modules.hasModule('register.payment.allow_zero_sale_total') ||
          !this.saleTotal.isZero() ||
          this.deposits.hasDeposits ||
          this.hasInvoicePayments ||
          options.complete === false ||
          payment instanceof CashPayment
        ) {
          // make sure the payment is assigned to the sale
          payment.model.set('saleID', sale.get('saleID'));

          // refunded payments might have a CCCharge attached; make sure we clear
          // it otherwise the API will complain
          if (payment.model.has('refPaymentID')) {
            payment.model.unset('CCCharge');
          }

          salePayments.add(payment.model);
        }
      });

      // add any deposit refunds to the sale
      _.each(this.deposits.depositRefunds, (deposit) => {
        // For deposit refunds, the payment that we set to the sale is distinguished only by
        // the fact that it's associated with a credit account ID.  When the sale is completed,
        // the API will notice that the payment has an assigned credit account ID and will thus
        // treat the 'payment' as a deposit refund.  Deposit refunds for payment service-backed
        // payments are treated differently because they must be committed immediately rather
        // than at sale completion time.
        salePayments.add(deposit.model);
        salePayments.add(deposit.depositPayment.model);
      });

      if (
        Modules.hasModule('customers.invoicing_integration.free_tier') &&
        options.complete === true
      ) {
        this._processInvoicingSale(options, sale, salePayments, deferred);
      } else {
        this._processSale(options, sale, salePayments, deferred);
      }
    } else {
      this.trigger(_Events.COMPLETE_SALE_FAILED);

      if (status.error === _Errors.INTERNAL) {
        window.console.error(status.msg);
      }

      deferred.reject(status);
    }

    return deferred.promise();
  }

  _processSale(options, sale, salePayments, deferred) {
    // Remove the sale lines from the sale if we're about to complete it.
    // Lets the backend calculate totals to remove discrepancies caused
    // by Price Rules / Discounts / etc. See CLD-13170.
    if (options.complete) {
      sale.unset('SaleLines');
    }

    if (salePayments.length) {
      // Each SalePayment object in the collection has a `paymentTypeID` and the linked PaymentType object.
      // Sending the PaymentType object could cause the Retail API to try to update the SalePayment object.
      // We don't want that to happen when updating the sale, so we remove the linked PaymentType, and only
      // keep the `paymentTypeID`.
      salePayments.forEach((salePayment) => {
        salePayment.unset('PaymentType');
      });

      sale.set('SalePayments', {
        SalePayment: salePayments.toJSON(),
      });
    }

    this._updateSaleOnServer(sale)
      .done(() => {
        deferred.resolve();
      })
      .fail((saleManagerStatus) => {
        deferred.reject(saleManagerStatus);
      });
  }

  async _processInvoicingSale(options, sale, salePayments, deferred) {
    const startTime = new Date(Date.now()).getTime();

    const invoicingError = this._validateInvoicingFlow(sale);
    const hasCreditAccountPayment =
      !this.deposits.hasDeposits && !!this._getCreditAccountPayment(salePayments);

    if (invoicingError) {
      this.trigger(_Events.COMPLETE_SALE_FAILED);
      deferred.reject(invoicingError);
      return;
    }

    try {
      if (this.hasInvoicePayments) {
        // Note: we're updating the cloned sale instead of the register sale so if the sale fails then the page
        // won't be updated to show a deposit when it's an invoice payment
        const deposit = PaymentFactory.createPayment(
          PaymentTypeManager.Types.CREDIT_ACCOUNT,
          -this.totalInvoicePaymentAmount,
          this.creditAccountID,
          sale.id
        );
        salePayments.add(deposit.model);
      }

      if (this.hasDepositPayments) {
        this._appendDepositPaymentsToCreditAccountPaymentType(sale, salePayments);
      }

      if (this._shouldUseReturnInstructionOnly()) {
        const originalSaleIds = await this._getOriginalSaleIdsFromSaleLines();
        await ReturnInstructionService.upsertReturnInstruction({
          creditAccountId: this.creditAccountID,
          customerId: this.customer?.id,
          saleId: sale.id,
          amount: this.totalCreditAccountPayments.abs().toNumber(), // API expects positive amount
          currency: window.merchantos.locale.currency_code,
          customer: this.customer?.attributes,
          originalSaleIds,
        });
      }

      if (!hasCreditAccountPayment) {
        if (this._shouldUseDepositInstructionOnly()) {
          await DepositInstructionService.upsertDepositInstruction({
            creditAccountId: this.creditAccountID,
            customerId: this.customer?.id,
            saleId: sale.id,
            amount: this.deposits.depositTotal.toNumber(),
            currency: window.merchantos.locale.currency_code,
            customer: this.customer?.attributes,
          });
        } else if (
          !Modules.hasModule('invoicing.api.v1.consume_deposit') ||
          !Modules.hasModule('invoicing.api.v1.payment_instructions')
        ) {
          const payload = await this._getInvoicingPayload(sale);
          const creditAccount = this.customer?.getRelation('CreditAccount', CreditAccount);
          const creditLimit = creditAccount ? creditAccount.get('creditLimit') : new Decimal(0);

          await PaymentService.makePayment(payload, creditLimit);
          const endTime = new Date(Date.now()).getTime();
          const duration = Math.abs(startTime - endTime) / 1000;

          datadogRum.addAction(LsInvoicingInvoicePaymentTime, {
            invoicesToPay: this.invoicePaymentOptions,
            duration,
            accountId: window.merchantos.session.getSystemCustomerID(),
            saleId: sale.id,
          });
        }
      }

      this._processSale(options, sale, salePayments, deferred);
    } catch (e) {
      window.console.error(e);
      this.trigger(_Events.COMPLETE_SALE_FAILED);
      deferred.reject(new SaleManagerStatus(_Errors.USER_FACING, 'Unable to complete the sale.'));
    }
  }

  _appendDepositPaymentsToCreditAccountPaymentType(sale, salePayments) {
    const depositCreditAccountPayment = PaymentFactory.createPayment(
      PaymentTypeManager.Types.CREDIT_ACCOUNT,
      this.totalDepositPaymentAmount,
      this.creditAccountID,
      sale.id
    );
    const existingCreditAccountPayment = this._getCreditAccountPayment(salePayments);

    if (existingCreditAccountPayment) {
      const totalAmount = new Decimal(existingCreditAccountPayment.get('amount')).plus(
        depositCreditAccountPayment.amount
      );
      existingCreditAccountPayment.set('amount', totalAmount);
    } else {
      salePayments.add(depositCreditAccountPayment.model);
    }
  }

  _getCreditAccountPayment(salePayments) {
    return salePayments.find((payment) => payment.get('paymentTypeID') === 4);
  }

  _getInvoicingPayload(sale) {
    return getInvoicingPayload(
      sale,
      this._invoicePaymentOptions,
      this._depositPaymentOptions,
      getSalePaymentsForInvoice(getProcessedPaymentsFromSale(sale), this.pendingPayments),
      this.deposits.depositTotal,
      this.pendingChange,
      this.getPaymentsOfType(PaymentType.Types.GIFT_CARD).toArray()
    );
  }

  _invoicingSaleIsInCompletableState(sale) {
    const isCustomerRequired =
      this.hasDepositPayments || this.hasInvoicePayments || this.deposits.hasDeposits;
    const hasCustomer = !!sale.getCustomer()?.id;
    const isCompleted = sale.get('completed') || sale.get('completeTime');

    /**
     * It may seem counter-intuitive to validate that the sale is already marked as completed, but the finishSale
     * flow marks the sale object as completed before processing anything else, so a sale would not be completable
     * unless already marked as completed.
     */
    const isCompletable = isCompleted && !sale.get('archived') && !sale.get('voided');

    return isCustomerRequired ? isCompletable && hasCustomer : isCompletable;
  }

  _validateInvoicingFlow(sale) {
    if (!this._invoicingSaleIsInCompletableState(sale)) {
      return new SaleManagerStatus(_Errors.USER_FACING, 'Sale is not in a completable state.');
    }

    if (this.deposits.hasDeposits) {
      if (this.hasDepositPayments) {
        return new SaleManagerStatus(
          _Errors.USER_FACING,
          'Cannot process a deposit with a deposit payment.'
        );
      }

      if (this.hasInvoicePayments) {
        return new SaleManagerStatus(
          _Errors.USER_FACING,
          'Cannot process a deposit and invoice payments in the same sale.'
        );
      }
    }

    if (this.hasInvoicePayments) {
      const hasCurrencyMismatch = this._arePaymentCurrenciesMismatched(
        this._invoicePaymentOptions.invoicesToPay
      );

      if (hasCurrencyMismatch) {
        return new SaleManagerStatus(
          _Errors.USER_FACING,
          `Cannot process invoice payment due to currency mismatch. Validate that all selected invoices are in ${window.merchantos.locale.currency_code}.`
        );
      }
    }

    if (this.hasDepositPayments) {
      const hasCurrencyMismatch = this._arePaymentCurrenciesMismatched(
        this._depositPaymentOptions.depositPayments
      );

      if (hasCurrencyMismatch) {
        return new SaleManagerStatus(
          _Errors.USER_FACING,
          `Cannot process deposit payment due to currency mismatch. Validate that all selected deposits are in ${window.merchantos.locale.currency_code}.`
        );
      }
    }
  }

  _arePaymentCurrenciesMismatched(payments) {
    return payments.some((payment) => payment.currency !== window.merchantos.locale.currency_code);
  }

  /**
   * Determines if the sale should use the deposit-instruction endpoint instead of make-payment.
   * True when the deposit_instructions module is enabled and the sale only has a deposit to add
   * (no invoice payments, no deposit payments being used).
   *
   * @returns {boolean}
   */
  _shouldUseDepositInstructionOnly() {
    return (
      Modules.hasModule('invoicing.api.v1.deposit_instructions') &&
      this.deposits.depositTotal.isGreaterThan(0) &&
      !this.hasInvoicePayments &&
      !this.hasDepositPayments
    );
  }

  /**
   * Determines if the sale should use the return-instruction endpoint.
   * True when:
   * - return_instructions module is enabled
   * - sale is a refund sale (negative total)
   * - sale has at least one credit account payment
   * - sale does NOT have deposit refunds (not implemented yet)
   *
   * @returns {boolean}
   */
  _shouldUseReturnInstructionOnly() {
    const hasCreditAccountPayment = this.creditAccountPayments.length > 0;
    const hasDepositRefunds = !this.deposits.refundTotal.isZero();

    return (
      Modules.hasModule('invoicing.api.v1.return_instructions') &&
      this.isRefundSale() &&
      hasCreditAccountPayment &&
      !hasDepositRefunds
    );
  }

  /**
   * Extracts unique original sale IDs from credit account payments in refundablePayments.
   * These are the transactions that the return should be applied against.
   *
   * @returns {Array<number>} An array of unique original sale IDs
   */
  _getOriginalSaleIdsFromRefundablePayments() {
    const originalSaleIds = new Set();

    this.refundablePayments.forEach((payment) => {
      const paymentType = payment.model.getRelation('PaymentType', PaymentType);
      if (paymentType && paymentType.get('type') === PaymentType.Types.CREDIT_ACCOUNT) {
        const saleId = payment.model.get('saleID');
        if (saleId) {
          originalSaleIds.add(saleId);
        }
      }
    });

    return Array.from(originalSaleIds);
  }

  /**
   * Extracts unique original sale IDs from all sale lines in the current return sale.
   * This iterates over ALL sale lines to find their parent sale lines and extract
   * the original sale IDs, ensuring all sales involved in the return are included.
   *
   * @returns {Promise<Array<number>>} A promise resolving to an array of unique original sale IDs
   */
  async _getOriginalSaleIdsFromSaleLines() {
    const saleLines = this.sale.get('SaleLines');

    if (!saleLines?.SaleLine?.length) {
      return [];
    }

    // Collect all unique parentSaleLineIDs
    const parentSaleLineIds = new Set();
    saleLines.SaleLine.forEach((line) => {
      if (line.parentSaleLineID > 0) {
        parentSaleLineIds.add(line.parentSaleLineID);
      }
    });

    if (parentSaleLineIds.size === 0) {
      return [];
    }

    // Fetch each parent sale line to get its saleID
    const originalSaleIds = new Set();
    const fetchPromises = Array.from(parentSaleLineIds).map((parentSaleLineID) => {
      const parentSaleLine = new SaleLine({ saleLineID: parentSaleLineID });
      return new Promise((resolve) => {
        parentSaleLine
          .fetch()
          .done(() => {
            const saleId = parentSaleLine.get('saleID');
            if (saleId) {
              originalSaleIds.add(saleId);
            }
            resolve();
          })
          .fail(() => {
            // Log error but continue - we don't want to fail the whole operation
            // if one parent sale line can't be fetched
            resolve();
          });
      });
    });

    await Promise.all(fetchPromises);

    return Array.from(originalSaleIds);
  }

  /**
   * Commits an individual payment to the server immediately.
   *
   * @param {Payment} payment     The payment to save to the server.
   * @returns {jQuery.Promise}
   */
  commitPaymentToServer(payment) {
    let sale = new Sale();
    let salePayments = new SalePaymentCollection();

    sale.clear();
    sale.set('saleID', this._sale.get('saleID'));
    salePayments.add(payment.model);
    if (salePayments.length) {
      sale.set('SalePayments', {
        SalePayment: salePayments.toJSON(),
      });
    }

    return this._updateSaleOnServer(sale);
  }

  /**
   * Helper method to sync the SaleManager sale data with the server.
   *
   * @param {Sale} sale   The sale model
   * @private
   */
  _updateSaleOnServer(sale) {
    let deferred = $.Deferred();
    let filter = new Filter().loadRelations('all');

    // finish the sale
    sale
      .save(filter)
      .done(() => {
        // once the sale has been completed update the managed sale and clear the pending
        // payments
        this._sale = sale;
        this._pendingPayments = {};

        // ...and show a signature capture for Retail Manager if necessary
        RetailManager.sendSignatureData(this.creditDebitPayments);

        this.deposits.setInSaleDepositRefunds(this.sale);

        this._calculatePayments();
        deferred.resolve();
      })
      .fail((response) => {
        if (
          typeof response.responseJSON.errorClass !== 'undefined' &&
          response.responseJSON.errorClass === 'SwipeError'
        ) {
          // If a bad swipe was the cause of the error, remove it
          // from the list of pending payments
          let swipePayment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.SWIPE,
            0,
            '',
            response.responseJSON.remoteReference
          );

          this.removePayment(swipePayment);
          this.removePayment(this.deposits.getDepositForPayment(swipePayment));
          this.deposits.removeDepositForPayment(swipePayment);

          this._calculatePayments();
          this.trigger(_Events.COMPLETE_SALE_FAILED);
        }

        // If we had a failure it could be because we've become out of sync with the server so
        // refresh the sale data and payments with the server.
        this.refreshSale().always(() => {
          deferred.reject(
            new SaleManagerStatus(_Errors.USER_FACING, response.responseJSON.message)
          );
        });
      });

    return deferred.promise();
  }

  /**
   * @deprecated by `addInSaleDeposit`
   * Adds a deposit to the sale.  This only applies the credit to the sale (which is not
   * committed until sale completion time).  Payments used to cover the deposit must
   * be handled by separate actions on the part of the user.
   *
   * @param {DepositPayment} deposit  The deposit payment
   * @param {isAccountPayment} isAccountPayment   true if the deposit is for an account payment
   * @returns {JQueryDeferred}   Resolves once the payment has been added
   */
  addDeposit(deposit, isAccountPayment = false) {
    let deferred = $.Deferred();
    if (deposit instanceof DepositPayment) {
      // The deposit total grabs the absolute total, while
      // the creditBalance is the sum of all credit payments (which is negative).
      // They both need the same sign.
      const creditBalance = this.getTotalPaymentsOfType(deposit.paymentTypeID).abs();

      if (!creditBalance.isEqualTo(this.deposits.depositTotal)) {
        // our in-sale credit account payments differ from our depositTotal.  This can happen
        // if a credit account payment was added before a deposit was added.  Just sync the
        // current amount to the deposit amount to clear out the prior amount.
        let synchronizingPayment = deposit.clone();
        synchronizingPayment.amount = new Decimal(this.deposits.depositTotal);
        this.addPayment(synchronizingPayment);
      }

      if (this.deposits.addDeposit(deposit, isAccountPayment)) {
        // we clone the deposit so that side-effects don't affect the data
        // we're tracking in the deposit
        this.addPayment(deposit.clone(), { merge: true })
          .done(() => {
            this._calculatePayments();
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        deferred.reject();
      }
    } else {
      window.console.error('Payment was not a deposit.');
      deferred.reject();
    }

    return deferred;
  }

  /**
   * Public
   * Adds a deposit to the sale. This only applies the credit to the sale (which is
   * immediately persisted in the sale). Payments used to cover the deposit must
   * be handled by separate actions on the part of the user.
   * @param deposit {Decimal} The deposit amount
   * @returns {Promise<void>}
   */
  async addInSaleDeposit(deposit) {
    try {
      await this.addDepositOnServer(deposit);
      await this.onInSaleDepositChanged();
    } catch (err) {
      throw new Error('Could not add the deposit.');
    }
  }

  /**
   * Removes all deposits from the sale.  This only removes the credit account amount
   * from the sale in order to conform to the legacy webPOS methodology.
   */
  async removeDeposits() {
    if (!this.deposits.hasDeposits) return;

    if (
      Modules.hasModule('register.payment.deposit_by_paylink') ||
      Modules.hasModule('customers.invoicing_integration.free_tier')
    ) {
      try {
        window.showNotifier('working');
        await this.removeDepositsOnServer();
        await this.onInSaleDepositChanged();
      } catch (error) {
        window.alertUser(
          Localize.translate(
            'Something went wrong. Try again or contact support if the problem persists.'
          )
        );
      } finally {
        window.hideNotifier('working');
      }
      return;
    }

    // We're mimicking the legacy UI here, so we only remove
    // the deposit itself and not the payment for the deposit.
    _.each(this.deposits.deposits, (deposit) => {
      this.removePayment(deposit);
    });
    this.deposits.clearDeposits();
    this._calculatePayments();
    if (Modules.hasModule('register.payment.refundable_credit_payments')) {
      this.trigger(_Events.DEPOSITS_CHANGED, this.availableCreditBalance);
    } else {
      this.trigger(_Events.DEPOSITS_CHANGED);
    }
  }

  async addDepositOnServer(deposit) {
    let previousDeposit = new Decimal(0);

    if (!Modules.hasModule('customers.invoicing_integration.free_tier')) {
      try {
        const response = await axios.get(`/admin/sale/${this.sale.id}/in-person-deposit`);
        if (response.data.data.amount) previousDeposit = new Decimal(response.data.data.amount);
      } catch (error) {
        /**
         * the get call will return 404 if there is an error with the endpoint,
         * or there is no in-person deposit currently, and the error is handled in the React component that calls this function.
         */
      }
    }

    try {
      await axios.post(`/admin/sale/${this.sale.id}/in-person-deposit`, {
        amount: previousDeposit.plus(new Decimal(deposit)).toNumber(),
      });
    } catch (err) {
      throw new Error('Could not add the deposit.');
    }
  }

  async removeDepositsOnServer() {
    try {
      // Delete deposit instruction from Invoicing first (if module enabled)
      if (Modules.hasModule('invoicing.api.v1.deposit_instructions')) {
        await DepositInstructionService.deleteDepositInstruction(
          this.creditAccountID,
          this.sale.id
        );
      }

      // Then delete from R-Series
      await axios.delete(`/admin/sale/${this.sale.id}/in-person-deposit`);
    } catch (error) {
      throw new Error('Could not remove the deposit.');
    }
  }

  /**
   * Fetch the latest Sale version from the server and refresh deposits based on in sale deposits.
   */
  async onInSaleDepositChanged() {
    try {
      await this.refreshSale();
      this.deposits.refreshDepositFromSale(this.sale, this.creditAccountID);
      this._calculatePayments();
      if (Modules.hasModule('register.payment.refundable_credit_payments')) {
        this.trigger(_Events.DEPOSITS_CHANGED, this.availableCreditBalance);
      } else {
        this.trigger(_Events.DEPOSITS_CHANGED);
      }
    } catch (error) {
      throw new Error('Failed to refresh the sale after deposit change.');
    }
  }

  /**
   * Adds a payment to the sale.  Pending payments are added to the pending payment list.
   *
   *
   * @param payment
   * @param {Object} options  Options to apply when adding the payment
   * @param {boolean} [options.merge] - Whether to merge (sum) a pending payment
   *   that does not allow multiples (cash, credit account, etc)  or replace it with
   *   the provided payment amount. Defaults to false.
   * @param {boolean} options.ignoreValidationErrors    When true, the payment will be added without
   *   validation (necessary to handle Manual Credit Card payments)
   * @param {Object} [options.lspayMonitoring] - Optional LspayTerminalMonitoring instance
   *   for reporting refresh sale retry events
   *
   * @returns {jQuery.Promise}    Resolves once payment has been added
   */
  addPayment(payment, options = {}) {
    let deferred = $.Deferred();

    if (
      payment &&
      (this.validatePayment(payment).error === SaleManager.Errors.NO_ERROR ||
        options.ignoreValidationErrors)
    ) {
      if (payment.commitImmediately && !payment.amount.isZero()) {
        payment
          .commit()
          .done(() => {
            // refresh the sale to obtain the new payment and total
            if (Modules.hasModule('payments.refresh_sale_retry')) {
              const maxRetries = 5;
              const retryDelay = 1000;

              (async () => {
                // eslint-disable-next-line no-await-in-loop
                for (let attempt = 0; attempt <= maxRetries; attempt++) {
                  if (attempt > 0) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                  }

                  try {
                    // eslint-disable-next-line no-await-in-loop
                    await this.refreshSale();
                  } catch {
                    continue;
                  }

                  const salePayments = this._sale.getRelation(
                    'SalePayments',
                    SalePaymentCollection
                  );
                  const salePayment = salePayments
                    ? salePayments.findWhere({ salePaymentID: payment.model.id })
                    : null;

                  if (salePayment) {
                    if (options.lspayMonitoring) {
                      options.lspayMonitoring.sendEvent('REFRESH_SALE_RETRY', {
                        outcome: 'resolved',
                        attempts: attempt + 1,
                        saleID: this._sale.id,
                        salePaymentID: payment.model.id,
                      });
                    }
                    deferred.resolve(payment.model.id);
                    return;
                  }
                }

                if (options.lspayMonitoring) {
                  options.lspayMonitoring.sendEvent('REFRESH_SALE_RETRY', {
                    outcome: 'rejected',
                    attempts: maxRetries + 1,
                    saleID: this._sale.id,
                    salePaymentID: payment.model.id,
                  });
                }
                deferred.reject();
              })();
            } else {
              this.refreshSale()
                .done(() => {
                  deferred.resolve(payment.model.id);
                })
                .fail(() => {
                  deferred.reject();
                });
            }
          })
          .fail(() => {
            deferred.reject();
          });
      } else {
        let pendingPayment = payment;
        if (!payment.allowMultiple) {
          // for payments that disallow multiple payments of the same type, just replace
          // the current pending payment
          let existingPendingPayment = _.find(
            this._pendingPayments,
            (currentPayment) => currentPayment.paymentTypeID === payment.paymentTypeID
          );

          if (existingPendingPayment) {
            if (options.merge) {
              existingPendingPayment.amount = existingPendingPayment.amount.plus(payment.amount);
            } else {
              existingPendingPayment.amount = payment.amount;
            }
            pendingPayment = existingPendingPayment;
          }
        }

        if (payment instanceof GiftCardPayment) {
          // Gift card payments only allow multiples for different gift cards.  If the same
          // gift card is added twice, it replaces the old payment like is the case for
          // payments that disallow multiple payments.
          let identicalGiftCardPayment = _.find(
            this._pendingPayments,
            (currentPayment) =>
              currentPayment.paymentTypeID === payment.paymentTypeID &&
              currentPayment.model.get('creditAccountID') === payment.model.get('creditAccountID')
          );

          if (identicalGiftCardPayment) {
            identicalGiftCardPayment.amount = identicalGiftCardPayment.amount.plus(payment.amount);
            pendingPayment = identicalGiftCardPayment;
          }
        }

        if (pendingPayment.amount.isZero()) {
          // we don't hold onto empty pending payments
          this.removePayment(pendingPayment);
        } else {
          // store the payment referenced by the model's internal ID
          this._pendingPayments[pendingPayment.model.get('remoteReference')] = pendingPayment;
        }

        this._calculatePayments();

        deferred.resolve(pendingPayment.model.get('remoteReference'));
      }
    } else {
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Removes a payment from the sale.  Pending payments are removed immediately whereas
   * existing payments (i.e. registered with the server) are removed via the API.
   *
   * @param {Payment} payment    The payment to remove
   * @returns {jQuery.Promise}   Resolves when payment has been removed
   */
  removePayment(payment) {
    let deferred = $.Deferred();
    // check for the payment in the pending IDs first
    if (!payment) {
      deferred.reject(`The payment is empty.`);
    } else {
      if (this._pendingPayments[payment.model.get('remoteReference')]) {
        delete this._pendingPayments[payment.model.get('remoteReference')];
        this._calculatePayments();
        this.refreshSale()
          .done(() => {
            deferred.resolve();
          })
          .fail(() => {
            deferred.reject();
          });
      } else if (payment.isServicePayment()) {
        if (payment.serviceModel) {
          payment.serviceModel
            .refundPayment({ refundTip: payment.refundTip })
            .done(() => {
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              deferred.reject();
            });
        } else {
          // no matching service payment was found
          deferred.reject();
        }
      } else {
        // the ID doesn't appear to be a pending payment so
        // let's see if it's a sale payment ID in the existing sale
        let salePayments = this._sale.getRelation('SalePayments', SalePaymentCollection);
        let salePayment = salePayments
          ? salePayments.findWhere({ salePaymentID: payment.model.id })
          : null;

        // The API only supports removing (i.e. archiving) payments through the sale currently
        if (salePayment) {
          salePayment.set('archived', true);
          salePayments.add(salePayment, { merge: true });
          this._setSalePayments(salePayments);
          this._sale
            .save(true)
            .done(() => {
              this._calculatePayments();
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail((errorMessage) => {
              deferred.reject(errorMessage);
            });
        } else {
          deferred.reject(`The salePayment is empty.`);
        }
      }
    }

    return deferred;
  }

  /**
   * Updates a pending payment in the sale.
   *
   * @param {*} payment A payment object
   */
  updatePaymentAmount(payment) {
    // currently only support updating payment amounts where multiples are not allowed;
    // this will likely change once V2 payments service is ready
    if (_.isObject(payment) && !payment.allowMultiple) {
      let existingPendingPayment = _.find(
        this._pendingPayments,
        (currentPayment) => currentPayment.paymentTypeID === payment.paymentTypeID
      );

      if (existingPendingPayment) {
        if (payment.amount.isZero()) {
          // remove the pending payment if the amount is zero.  This is necessary
          // so we don't accidentally send a zero payment when completing a sale
          this.removePayment(existingPendingPayment);
        } else {
          existingPendingPayment.amount = payment.amount;

          this._pendingPayments[existingPendingPayment.model.get('remoteReference')] =
            existingPendingPayment;
        }

        this._calculatePayments();
      }
    } else {
      window.console.error('A payment must be specified to update.');
    }
  }

  /**
   * Voids a payment using a terminal.  The terminal is invoked to perform any terminal actions
   * required to the void the payment (e.g. in cases where there is no API support for a particular
   * terminal).  Once that operation is complete, the payment is removed from the sale manager.
   *
   * @param {Terminal} terminal   The terminal to void the transaction
   * @param {Payment} payment     The payment to be voided
   * @returns {*}
   */
  voidTerminalPayment(payment, terminal) {
    let deferred = $.Deferred();

    if (terminal && payment) {
      terminal
        .voidTransaction(payment)
        .done(() => {
          // the terminal should have archived the payment so sync
          // the sale manager
          deferred = this.removePayment(payment);
        })
        .fail((response) => {
          window.console.error('Failed to void the transaction:', response);
          deferred.reject();
        });
    } else {
      window.console.error('Cannot void payment:', payment, terminal);
      deferred.reject();
    }

    return deferred.promise();
  }

  /**
   * Determines if there is an existing pending payment of the type of payment specified.
   *
   * @param {*} payment   The payment of the type to be matched to any existing payments
   * @returns {boolean}
   */
  hasExistingPaymentOfType(payment) {
    let totalPaymentsOfType = this.getTotalPaymentsOfType(payment.paymentTypeID);

    return !totalPaymentsOfType.isZero();
  }

  /**
   * Reports if there are pending payments currently in the sale.
   *
   * @return {boolean} True if there are pending payments in the sale
   */
  hasPendingPayments() {
    return this._pendingPayments && !_.isEmpty(this._pendingPayments);
  }

  /**
   * Returns the total amount of payments of the type specified that exist in the sale.  Only accounts
   * for pending payments.
   *
   * @param {number|string} paymentTypeID The payment type ID of the pending payment to retrieve
   * @returns {Decimal}   The total amount of pending payments of the type specified
   */
  getTotalPaymentsOfType(paymentTypeID) {
    let total = new Decimal(0);

    _.each(this._pendingPayments, (currentPayment) => {
      if (currentPayment.paymentTypeID == paymentTypeID) {
        total = total.plus(currentPayment.amount);
      }
    });

    return total;
  }

  /**
   * Determines if the specified payment may be added to the sale.
   *
   * @returns {SaleManagerStatus} The status of the operation.  Returns a status
   *                              with NO_ERROR if payments can be accepted.
   */
  validatePayment(payment) {
    let status = new SaleManagerStatus();
    let hasSaleLines = this.sale.has('SaleLines');

    const checkZeroSaleTotal = Modules.hasModule('register.payment.allow_zero_sale_total')
      ? !hasSaleLines && this.saleTotal.isZero() // allows zero sale total if there is at least one sale line
      : this.saleTotal.isZero();

    const originalConditionCheck =
      checkZeroSaleTotal &&
      !this.deposits.hasDeposits &&
      !(payment instanceof DepositPayment) &&
      payment &&
      !payment.amount.isZero(); // we'll allow clearing payments

    const conditionCheckWithInvoicingFlow =
      originalConditionCheck && !this.totalInvoicePaymentAmount;

    const conditionCheck = Modules.hasModule('customers.invoicing_integration.free_tier')
      ? conditionCheckWithInvoicingFlow
      : originalConditionCheck;

    if (!this.sale.has('displayableTotal')) {
      status = new SaleManagerStatus(_Errors.INTERNAL, 'Invalid sale received from API.');
    } else if (conditionCheck) {
      let amount = Format.formatMoney(this.saleTotal);
      status = new SaleManagerStatus(
        _Errors.USER_FACING,
        `You can not enter a payment because the sale total is <var>${amount}</var>.`
      );
    } else if (
      payment &&
      !(payment instanceof CashPayment) &&
      !(payment instanceof DepositPayment)
    ) {
      try {
        let currentPaymentOfTypeThatDisallowsMultiples = new Decimal(0);
        if (!payment.allowMultiple) {
          // Payments that disallow multiple payments of the same type per sale must be
          // excluded when we're determining if we can add the payment to the sale because
          // the payment we're checking will replace the old when when we add it to the sale.
          currentPaymentOfTypeThatDisallowsMultiples = this.getTotalPaymentsOfType(
            payment.paymentTypeID
          );
        }

        let currentPaymentForSameGiftCard = new Decimal(0);
        if (payment instanceof GiftCardPayment) {
          // As with most things, gift cards are special in that we allow multiple payments
          // to different cards but only one payment for each card within a specific sale.  So,
          // when determining if we can add the new gift card payment to the sale we need to
          // exclude any prior payment that we added to the sale.
          let identicalGiftCardPayment = _.find(
            this._pendingPayments,
            (currentPayment) =>
              currentPayment.model.get('creditAccountID') === payment.model.get('creditAccountID')
          );

          if (identicalGiftCardPayment) {
            currentPaymentForSameGiftCard = identicalGiftCardPayment.amount;
          }
        }

        if (this.isRefundSale()) {
          if (
            payment.amount
              .plus(this.pendingPaymentsTotal)
              .minus(currentPaymentOfTypeThatDisallowsMultiples)
              .minus(currentPaymentForSameGiftCard)
              .minus(this.deposits.depositTotal)
              .minus(payment.tipAmount || 0)
              .minus(Modules.hasModule('payments.surcharge') ? payment.surchargeAmount || 0 : 0)
              .minus(this.calcTips || 0)
              .minus(Modules.hasModule('payments.surcharge') ? this.calcSurcharges || 0 : 0)
              .isLessThan(this.saleTotal)
          ) {
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a refund greater than the total.'
            );
          }
        } else if (
          payment.amount
            .plus(this.pendingPaymentsTotal)
            .minus(currentPaymentOfTypeThatDisallowsMultiples)
            .minus(currentPaymentForSameGiftCard)
            .minus(this.cashPayment)
            .minus(this.deposits.depositTotal)
            .minus(payment.tipAmount || 0)
            .minus(Modules.hasModule('payments.surcharge') ? payment.surchargeAmount || 0 : 0)
            .minus(this.calcTips || 0)
            .minus(Modules.hasModule('payments.surcharge') ? this.calcSurcharges || 0 : 0)
            .minus(
              Modules.hasModule('customers.invoicing_integration.free_tier')
                ? this.totalDepositPaymentAmount
                : 0
            )
            .isGreaterThan(this.saleTotal)
        ) {
          // we have too many payments (other than cash payments)
          status = new SaleManagerStatus(
            _Errors.USER_FACING,
            'You can not enter a payment greater than the sale total.'
          );
        }
      } catch (err) {
        status = new SaleManagerStatus(
          _Errors.INTERNAL,
          'Invalid Amount Entered, UI amount will be reset to the previous valid amount'
        );
      }
    }

    return status;
  }

  /**
   * Determines if the managed sale is a sale refund.  Currently this is solely determined
   * by whether the sale total is a negative amount.
   *
   * @returns {*}
   */
  isRefundSale() {
    let isRefundSale;

    try {
      let saleTotal = new Decimal(this.saleTotal);
      isRefundSale = saleTotal.isNegative();
    } catch (error) {
      isRefundSale = false;
    }

    return isRefundSale;
  }

  /**
   * Coverts the sale manager data into an object.  Useful for passing data into
   * a template for use rather than specifying individual properties.  The name of
   * the method is chosen to be compatible with JSON.stringify.
   */
  toJSON() {
    const dto = {
      sale: this._sale ? this._sale.toJSON() : new Sale().toJSON(),
      saleTotal: this.saleTotal,
      totalPayments: this.pendingPaymentsTotal,
      balance: this.pendingBalance,
      change: this.pendingChange,
      cashPayment: this.cashPayment,
      isRefundSale: this.isRefundSale(),
    };
    if (Modules.hasModule('register.sales.cash_rounding')) {
      const cashRoundingDTO = {
        ...dto,
        isCashRoundingActive: this.isCashRoundingActive,
        cashRoundingDelta: this.cashRoundingDelta,
        cashRoundedTotal: this.cashRoundedTotal,
        cashRoundedBalance: this.cashRoundedBalance,
        cashRoundedCashMax: this.cashRoundedCashMax,
        cashRoundedChange: this.cashRoundedChange,
      };
      if (Modules.hasModule('register.sales.cash_rounding.debug') && this._cashRoundingOptions) {
        cashRoundingDTO.cashRoundingDebug = {
          denomination: this._cashRoundingOptions.denomination,
          rule: this._cashRoundingOptions.rule,
        };
      }
      return cashRoundingDTO;
    }
    return dto;
  }

  /**
   * Determines if the sale has any pending credit card payments.  This is primarily for
   * iZettle payments which have no back-end integration with the server and is only supported
   * with Retail Manager.
   *
   * @returns {boolean}   True if there are uncommitted payments with the server.
   */
  hasUnsavedCreditPayments() {
    let unsavedPayments = _.find(
      this._pendingPayments,
      (payment) => payment._model.get('PaymentType')?.type === PaymentType.Types.CREDIT_CARD
    );

    return !!unsavedPayments;
  }

  addServiceDepositRefundToSale(depositRefund) {
    let deferred = $.Deferred();
    if (depositRefund.depositPayment.isServicePayment()) {
      // set the credit account payment on the sale
      this.commitPaymentToServer(depositRefund)
        .done(() => {
          this.deposits
            .refund(depositRefund)
            .done(() => {
              this.refreshSale()
                .done(() => {
                  deferred.resolve();
                })
                .fail(() => {
                  deferred.reject();
                });
            })
            .fail(() => {
              // the deposit payment failed so archive the balancing credit account payment
              depositRefund.model.set('archived', true);
              this.commitPaymentToServer(depositRefund);

              deferred.reject();
            });
        })
        .fail(() => {
          deferred.reject();
        });
    }

    return deferred.promise();
  }

  /**
   * Updates the SalePayments relation in the Sale model with the attributes from a
   * SalePaymentCollection.
   *
   * @param {SalePaymentCollection} salePayments  The sale payments to be written to the Sale model
   * @private
   */
  _setSalePayments(salePayments) {
    // need to conform to the wacky API representation
    this._sale.set('SalePayments', {
      SalePayment: salePayments.toJSON(),
    });
  }

  /**
   * Calculates the total, balance and change due for any existing pending payments.  The
   * calculated values are exposed as pending payment properties on the SaleManager.
   *
   * @private
   */
  _calculatePayments() {
    try {
      let totalInSalePayments = this.totalInSalePayments.plus(
        Modules.hasModule('customers.invoicing_integration.free_tier')
          ? this.totalDepositPaymentAmount
          : 0
      );
      let change = new Decimal(0);
      let balance = new Decimal(0);
      let totalCashPayments = Modules.hasModule('register.sales.cash_rounding')
        ? this.totalInSaleCashPayments
        : new Decimal(0);
      let saleTotal = new Decimal(this.saleTotal);
      let calcTips = this.sale.get('calcTips') || new Decimal(0);
      let calcSurcharges = Modules.hasModule('payments.surcharge')
        ? this.sale.get('calcSurcharges') || new Decimal(0)
        : new Decimal(0);

      // Calculate in pendingPayments as part of the totalInSalePayments
      if (!_.isEmpty(this._pendingPayments)) {
        // Finding all the payments that are non (NOT) CashPayment or DepositPayments
        let nonCashOrDepositPayments = _.reject(
          this._pendingPayments,
          (payment) => payment instanceof CashPayment || payment instanceof DepositPayment
        );
        // Finding all the payments that are cash payments, normally there should be only one instance
        let cashPayments = _.filter(
          this._pendingPayments,
          (payment) => payment instanceof CashPayment
        );

        // Adding non-cash and deposit pending payments to the totalInSalePayments
        _.each(nonCashOrDepositPayments, (payment) => {
          payment.amount = payment.amount.toFixed(Localize.getCurrencyPrecision());
          totalInSalePayments = totalInSalePayments.plus(payment.amount);
          calcTips = calcTips.plus(payment.tipAmount || 0);
          calcSurcharges = calcSurcharges.plus(
            Modules.hasModule('payments.surcharge') ? payment.surchargeAmount || 0 : 0
          );
        });

        let owesMoneyToCustomer = saleTotal.minus(totalInSalePayments).isLessThan(0);

        // Adding the cash payments to the totalInSalePayments
        _.each(cashPayments, (payment) => {
          payment.amount = payment.amount.toFixed(Localize.getCurrencyPrecision());
          totalCashPayments = totalCashPayments.plus(payment.amount);
        });

        totalInSalePayments = totalInSalePayments.plus(totalCashPayments);

        // Calculating the change
        if (!owesMoneyToCustomer) {
          // the calculated totalInSalePayments includes deposits so make sure we take that into account
          // when calculating the change due
          const saleTotalWithDeposits = saleTotal.plus(this.deposits.depositTotal);
          if (totalInSalePayments.isGreaterThan(saleTotalWithDeposits)) {
            change = totalInSalePayments
              .minus(saleTotalWithDeposits)
              .minus(calcTips)
              .minus(Modules.hasModule('payments.surcharge') ? calcSurcharges || 0 : 0);
          }
        }
      }

      // The balance is negative number because the totalInSalePayments is higher than the saleTotal,
      // so forth we need to exclude the depositTotal and the calcTips, calcSurcharges by adding them to the balance.
      balance = saleTotal
        .minus(totalInSalePayments.minus(change))
        .plus(this.deposits.depositTotal)
        .plus(calcTips)
        .plus(Modules.hasModule('payments.surcharge') ? calcSurcharges : 0);

      if (Modules.hasModule('register.sales.cash_rounding') && this.isCashRoundingActive) {
        const balanceWithoutCash = saleTotal
          .plus(this.deposits.depositTotal)
          .minus(totalInSalePayments.minus(totalCashPayments));
        const roundedBalanceWithoutCash = calculateCashRoundingAmount(
          balanceWithoutCash,
          this.cashRoundingOptions
        );
        // Cash rounding is satisfied when:
        // 1. The exact balance is zero, OR
        // 2. The cash payment equals the rounded balance (absorbing the rounding delta)
        const isCashRoundingSatisfied =
          balance.isZero() || totalCashPayments.eq(roundedBalanceWithoutCash);

        if (isCashRoundingSatisfied) {
          if (!change.isZero()) {
            this.cashRoundingDelta = balanceWithoutCash.minus(roundedBalanceWithoutCash);
            this.cashRoundedChange = totalCashPayments.minus(roundedBalanceWithoutCash);
          } else {
            this.cashRoundingDelta = balanceWithoutCash.minus(roundedBalanceWithoutCash);
            this.cashRoundedChange = new Decimal(0);
          }
          // Balance is satisfied via cash rounding
          this.cashRoundedBalance = new Decimal(0);
        } else {
          this.cashRoundingDelta = calculateCashRoundingDelta(balance, this.cashRoundingOptions);
          this.cashRoundedChange = new Decimal(0); // change cannot have a value until the balance is satisfied
          this.cashRoundedBalance = balance.minus(this.cashRoundingDelta);
        }
        this.cashRoundedCashPayment = calculateCashRoundingAmount(
          totalCashPayments,
          this.cashRoundingOptions
        );
        this.cashRoundedCashMax = roundedBalanceWithoutCash;
      }

      // We make sure to update the corresponding attributes in the SaleManger with the right value
      // from the sale
      if (Modules.hasModule('register.sales.cash_rounding')) {
        // Update cashPayment before pendingChange change:balance event is triggered
        this.cashPayment = totalCashPayments;
      }
      this.pendingPaymentsTotal = totalInSalePayments;
      this.pendingBalance = balance;
      this.pendingChange = change;
      if (!Modules.hasModule('register.sales.cash_rounding')) {
        this.cashPayment = totalCashPayments;
      }
      this.calcTips = calcTips;
      this.calcSurcharges = calcSurcharges;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Determines if the sale can be finished.
   *
   * @returns {SaleManagerStatus} The status of the operation.  Returns a status
   *                              with NO_ERROR if the sale can be finished.
   * @private
   */
  _canFinishSale() {
    let status = new SaleManagerStatus();
    if (this._sale) {
      if (this.sale.has('displayableTotal')) {
        let saleTotal;
        try {
          saleTotal = new Decimal(this.sale.get('displayableTotal'));
        } catch (err) {
          // bad data came back from the API
          status = new SaleManagerStatus(_Errors.INTERNAL, 'Invalid sale received from API.');
          throw err;
        }

        const calcTips = this.calcTips;
        const calcSurcharges = this.calcSurcharges;
        if (this.isRefundSale()) {
          if (this.pendingPaymentsTotal.isGreaterThan(saleTotal)) {
            // we don't have enough refunds to finish the sale
            status = new SaleManagerStatus(_Errors.USER_FACING, 'Refunds are insufficient.');
          }

          if (
            this.pendingPaymentsTotal
              .minus(calcTips)
              .minus(Modules.hasModule('payments.surcharge') ? calcSurcharges : 0)
              .isLessThan(saleTotal)
          ) {
            // we have too many refunds (other than cash payments)
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a refund greater than the total.'
            );
          }

          if (Modules.hasModule('register.sales.cash_rounding') && this.isCashRoundingActive) {
            if (status.isError() && this.isBalanceSatisfiedWithCashRounding) {
              status = new SaleManagerStatus();
            }
          }
        } else {
          let isInvalidSalePayment = this.pendingPaymentsTotal
            .minus(this.deposits.depositTotal)
            .minus(calcTips)
            .minus(Modules.hasModule('payments.surcharge') ? calcSurcharges : 0)
            .isLessThan(saleTotal);
          let isInvalidDepositPayment =
            this.deposits.hasDeposits &&
            this.pendingPaymentsTotal.isLessThan(this.deposits.depositTotal);
          let isInvalidInvoicePayment =
            Modules.hasModule('customers.invoicing_integration.free_tier') &&
            this.hasInvoicePayments &&
            this.pendingPaymentsTotal.isLessThan(this.totalInvoicePaymentAmount);

          // When cash rounding has already absorbed the cent-level shortfall, none of
          // the three "is < target" checks above should block the sale. Previously
          // only `isInvalidSalePayment` was forgiven, which silently rejected any
          // invoice-payment or deposit sale where cash rounding was applied.
          if (
            Modules.hasModule('register.sales.cash_rounding') &&
            this.isCashRoundingActive &&
            this.isBalanceSatisfiedWithCashRounding
          ) {
            isInvalidSalePayment = false;
            isInvalidDepositPayment = false;
            isInvalidInvoicePayment = false;
          }

          if (isInvalidSalePayment || isInvalidDepositPayment || isInvalidInvoicePayment) {
            // we don't have enough payments to finish the sale
            status = new SaleManagerStatus(_Errors.USER_FACING, 'Payments are insufficient.');
          }

          let paymentsTotal = this.pendingPaymentsTotal
            .minus(this.cashPayment)
            .minus(this.deposits.depositTotal)
            .minus(calcTips)
            .minus(Modules.hasModule('payments.surcharge') ? calcSurcharges : 0);

          if (Modules.hasModule('customers.invoicing_integration.free_tier')) {
            paymentsTotal = paymentsTotal.minus(this.totalInvoicePaymentAmount);
          }

          if (paymentsTotal.isGreaterThan(saleTotal)) {
            // we have too many payments (other than cash payments)
            status = new SaleManagerStatus(
              _Errors.USER_FACING,
              'You can not enter a payment greater than the sale total.'
            );
          }
        }
      } else {
        status = new SaleManagerStatus(_Errors.INTERNAL, 'The sale has no total.');
      }
    } else {
      status = new SaleManagerStatus(_Errors.INTERNAL, 'There is no sale to finish.');
    }

    return status;
  }

  /**
   * Event handler for when the Sale's total changes.
   *
   * @private
   */
  _onTotalChanged() {
    this._calculatePayments();
  }

  /**
   * Retrieves payment of the type specified from the sale and pending payments.
   *
   * @returns {SalePaymentCollection} A collection of sale payments.
   */
  getPaymentsOfType(paymentType) {
    const salePayments =
      this.sale.getRelation('SalePayments', SalePaymentCollection) || new SalePaymentCollection();

    _.each(this._pendingPayments, (payment) => {
      salePayments.add(payment.model);

      if (payment.details) {
        // Terminal payments may have details that very so normalize where we
        // story the data with the model by attaching it to the model directly
        // rather than a model attribute.  This is primarily used to display
        // the appropriate data in the listing view.  We can get rid of this
        // if/when we re-factor the payment listing to use a custom listing view
        // rather than the Marionette listing view.
        payment.model.details = payment.details;
      }

      if (payment.transactionReferenceID) {
        // iZettle payments have a reference ID.  Expose that on the model
        // so that we can reference it but it's NOT a model property.
        payment.model.transactionReferenceID = payment.transactionReferenceID;
      }
    });

    return new SalePaymentCollection(
      salePayments.filter((salePayment) => {
        const paymentTypeRelation = salePayment.getRelation('PaymentType', PaymentType);
        const isValidPendingPayment =
          !salePayment.get('archived') && !salePayment.get('amount').isZero();

        if (isValidPendingPayment) {
          if (Modules.hasModule('register.payment.refundable_credit_payments')) {
            /**
             * If the Payment Collection is not coming from the Payment Service,
             * we fallback on the client-side version of the payment types.
             *
             * The supported type should be [CREDIT_CARD, GIFT_CARD, USER_DEFINED, CREDIT_ACCOUNT]
             */
            if (!this._isPaymentCollectionFromService) {
              return (
                paymentTypeRelation &&
                [
                  PaymentType.Types.CREDIT_CARD, // This is type needs to be present for the legacy gateway that might still use it.
                  PaymentType.Types.GIFT_CARD, // This is one of the default type that can't be archived that need to be present.
                  PaymentType.Types.USER_DEFINED, // This is the type returned for all the custom payment types
                  PaymentType.Types.CREDIT_ACCOUNT, // This is the type returned for all the custom payment types
                ].includes(paymentTypeRelation.get('type'))
              );
            }
          }
          /**
           * If the Payment Collection is not coming from the Payment Service,
           * we fallback on the client-side version of the payment types.
           *
           * The supported type should be [CREDIT_CARD, GIFT_CARD, USER_DEFINED]
           */
          if (!this._isPaymentCollectionFromService) {
            return (
              paymentTypeRelation &&
              [
                PaymentType.Types.CREDIT_CARD, // This is type needs to be present for the legacy gateway that might still use it.
                PaymentType.Types.GIFT_CARD, // This is one of the default type that can't be archived that need to be present.
                PaymentType.Types.USER_DEFINED, // This is the type returned for all the custom payment types
              ].includes(paymentTypeRelation.get('type'))
            );
          }

          return paymentTypeRelation && paymentTypeRelation.get('type') === paymentType;
        }

        return false;
      })
    );
  }

  /**
   * Gets the list of Refundable Payment types on the current sale
   * and sets their minimum/maximum refund value
   *
   * @returns {Array} - An array of refundable payment types
   */
  getRefundablePaymentTypes() {
    // We add a fake "Refund As Any" payment to handle ad hoc refunds;
    // this needs to be recalculated if the sale total changes. However
    // it's pretty harmless to recalculate it whenever this function is called
    this.refundablePayments = this.refundablePayments.filter(
      (refundablePayment) => refundablePayment.model.get('PaymentType').paymentTypeID
    );

    // Deposits work by adding a negative charge to a non-refund sale.
    // We want to remove these charges from the list of Refundable Payments-
    // They're not refundable payments, they're a hack to get everything else
    // to behave
    this.refundablePayments = this.refundablePayments.filter(
      (refundablePayment) => !refundablePayment.amount.isNegative()
    );

    // Now we're all cleaned up!

    // In the case of an ad-hoc refund, we want to pretend there was
    // a Refund as Any payment whose amount is the difference between
    // the sum of the original payments and the current pending balance
    // (e.g. original payments were $15, refund total is -$17; create a
    // refund as any payment for $2)
    let sumOfOriginalPayments = new Decimal(0);
    this.refundablePayments.forEach((refundablePayment) => {
      sumOfOriginalPayments = sumOfOriginalPayments.minus(refundablePayment.amount);
    });

    if (this.saleTotal.isLessThan(sumOfOriginalPayments)) {
      let difference = this.saleTotal.minus(sumOfOriginalPayments).abs();
      let blankPaymentType = new PaymentType({
        type: PaymentType.Types.USER_DEFINED,
      });

      let salePayment = new SalePayment({
        amount: difference,
        PaymentType: blankPaymentType.toJSON(),
      });

      this.refundablePayments = this.refundablePayments.add(
        PaymentFactory.createPaymentFromModel(salePayment)
      );
    }

    let refundablePaymentTypes = PaymentTypeManager.getRefundablePaymentTypes(
      this.refundablePayments
    );
    refundablePaymentTypes = refundablePaymentTypes.toJSON();
    refundablePaymentTypes =
      this._setMinimumPaymentPerRefundablePaymentType(refundablePaymentTypes);
    refundablePaymentTypes =
      this._setMaximumPaymentPerRefundablePaymentType(refundablePaymentTypes);

    return refundablePaymentTypes;
  }

  /**
   * Gets a particular refundable payment type after they've been initialized
   * with minimum/maximum refund values
   *
   * @param paymentTypeID - The ID of the payment type
   */
  getRefundablePaymentTypeByID(paymentTypeID) {
    return _.find(
      this.getRefundablePaymentTypes(),
      (refundablePaymentType) => refundablePaymentType.paymentTypeID === paymentTypeID
    );
  }

  /**
   * Determines if the the refunded payment is a refund a payment currently in the sale.
   *
   * @param {Payment} servicePayment  The refund service payment
   */
  isRefundOfPaymentInSale(servicePayment) {
    let isRefundPayment = false;
    if (servicePayment.get('operationType') === PaymentModel.OperationType.REFUND) {
      // if this is a refund for a payment in the same sale then ignore this payment
      isRefundPayment =
        this._servicePayments.findWhere({
          paymentID: servicePayment.get('refundedPaymentID'),
        }) instanceof PaymentModel;
    }

    return isRefundPayment;
  }

  /**
   * Sets the minimum amount of money we have to refund per
   * payment type, if any
   *
   * @param refundablePaymentTypes     The list of refundable Payment Types
   * @returns {Array} - The modified array of refundable payment types
   * @private
   */
  _setMinimumPaymentPerRefundablePaymentType(refundablePaymentTypes) {
    _.each(refundablePaymentTypes, (refundablePaymentType) => {
      // Get the sum of all payments that -need- to be refunded as this type,
      // as well as the sum of all payments that don't
      let refundAsThisType = new Decimal(0);
      let refundAsOtherType = new Decimal(0);
      this.refundablePayments.forEach((refundablePayment) => {
        if (
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID ===
          refundablePaymentType.paymentTypeID
        ) {
          refundAsThisType = refundAsThisType.minus(refundablePayment.amount);
        } else {
          refundAsOtherType = refundAsOtherType.minus(refundablePayment.amount);
        }
      });

      // Check if the sum of all other types are sufficient to complete the sale
      // If it is, great! The minimum is 0. Otherwise, a minimum exists and must
      // be calculated for Payment Types that are not set to Refund As Any.
      refundablePaymentType.minimumRefund = new Decimal(0);
      if (refundAsOtherType.isGreaterThan(this.saleTotal)) {
        refundablePaymentType.minimumRefund = this.saleTotal.minus(refundAsOtherType);
      }
    });

    return refundablePaymentTypes;
  }

  /**
   * Sets the maximum amount of money we can refund per
   * payment type, if any
   *
   * @param refundablePaymentTypes     The list of refundable Payment Types
   * @returns {Array} - The modified array of refundable payment types
   * @private
   */
  _setMaximumPaymentPerRefundablePaymentType(refundablePaymentTypes) {
    _.each(refundablePaymentTypes, (refundablePaymentType) => {
      refundablePaymentType.maximumRefund = new Decimal(0);

      this.refundablePayments.forEach((refundablePayment) => {
        if (
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID ===
            refundablePaymentType.paymentTypeID ||
          refundablePayment.model.get('PaymentType').refundAsPaymentTypeID === 0
        ) {
          refundablePaymentType.maximumRefund = refundablePaymentType.maximumRefund.plus(
            refundablePayment.amount
          );
        }
      });

      refundablePaymentType.maximumRefund = refundablePaymentType.maximumRefund.negated();

      if (
        !refundablePaymentType.maximumRefund.eq(0) &&
        refundablePaymentType.maximumRefund.isLessThan(this.saleTotal)
      ) {
        refundablePaymentType.maximumRefund = this.saleTotal;
      }
    });

    return refundablePaymentTypes;
  }
}
